// SaleOrderApprovalPage.js - DOLLAR NATIVE VERSION
// All values are stored and displayed in USD - no exchange rate conversion needed

import React, { useState, useMemo, useEffect, useCallback, useRef } from 'react';
import useFirestore from '../../hooks/useFirestore';
import useAuth from '../../hooks/useAuth';
import usePagePermissions from '../../hooks/usePagePermissions';
import { db } from '../../firebaseConfig';
import {
    addDoc,
    collection,
    serverTimestamp,
    runTransaction,
    getDocs,
    getDoc,
    doc,
    query,
    where
} from 'firebase/firestore';
import { useNotifications } from '../../contexts/NotificationContext';
import { getModuleConfig } from '../../config/notificationConfig';
import ConfirmDialog from '../../components/common/ConfirmDialog/ConfirmDialog';
import Toast from '../../components/common/Toast/Toast';
import SearchBar from '../../components/common/SearchBar/SearchBar';
import Pagination from '../../components/common/Pagination/Pagination';
import TableSkeleton from '../../components/common/TableSkeleton/TableSkeleton';
import EmptyState from '../../components/common/EmptyState/EmptyState';
import PageHeader from '../../components/common/PageHeader/PageHeader';
import StatusBadge from '../../components/common/StatusBadge/StatusBadge';
import ViewOrderModal from '../../components/common/ViewOrderModal/ViewOrderModal';
import TrackingModal from '../../components/common/TrackingModal/TrackingModal';
import StockEntryModal from '../../components/common/StockEntryModal/StockEntryModal';
import PackingModal from '../../components/common/PackingModal/PackingModal';
import styles from './Orders.module.css';
import CommercialInvoicePreview from '../../components/common/CommercialInvoicePreview/CommercialInvoicePreview';
import PackingListPreview from '../../components/common/PackingListPreview/PackingListPreview';
import InvoiceInfoModal from './InvoiceInfoModal';

// React Icons
import {
    FiX,
    FiArrowLeft,
    FiRefreshCw,
    FiCheck,
    FiAlertCircle,
    FiFileText,
    FiMapPin,
    FiList,
    FiEye,
    FiCheckCircle,
    FiXCircle,
    FiPackage,
    FiClipboard,
    FiBox,
    FiDollarSign,
    FiTrendingUp,
    FiInfo,
    FiDownload,
    FiGrid,
    FiLayers,
} from 'react-icons/fi';
import { FaFileInvoiceDollar } from "react-icons/fa";
import { FiDollarSign as FiMoney } from 'react-icons/fi';
import { MdBusiness } from 'react-icons/md';
import { FaIndustry, FaPrint } from 'react-icons/fa';

// ============================================
// 1. CONSTANTES E DEFINIÇÕES
// ============================================
const SALES_ORDERS_COLLECTION = 'sales-orders';
const PRODUCTS_COLLECTION = 'products';
const ITEMS_COLLECTION = 'items';
const PRODUCTION_ORDERS_COLLECTION = 'production-orders';
const CURRENT_STOCK_COLLECTION = 'current-stock';
const ITEMS_PER_PAGE = 10;

// Status do Pedido de Venda
const ORDER_STATUS = {
    DRAFT: 'draft',
    PENDING: 'pending',
    APPROVED: 'approved',
    IN_PRODUCTION: 'in_production',
    PRODUCTION_COMPLETED: 'production_completed',
    READY_FOR_PICKING: 'ready_for_picking',
    SHIPPED: 'shipped',
    DELIVERED: 'delivered',
    CANCELLED: 'cancelled'
};

// Status da Ordem de Produção
const PRODUCTION_STATUS = {
    AWAITING_MP: 'awaiting-mp',
    AWAITING_START: 'awaiting-start',
    PENDING_DEDUCTION: 'pending-deduction',
    ACTIVE: 'active',
    COMPLETED: 'completed'
};

const ORDER_STATUS_LABELS = {
    [ORDER_STATUS.DRAFT]: { label: 'Rascunho', className: 'statusBadgeDraft' },
    [ORDER_STATUS.PENDING]: { label: 'Pendente', className: 'statusBadgePending' },
    [ORDER_STATUS.APPROVED]: { label: 'Aprovado', className: 'statusBadgeApproved' },
    [ORDER_STATUS.IN_PRODUCTION]: { label: 'Em Produção', className: 'statusBadgeInfo' },
    [ORDER_STATUS.PRODUCTION_COMPLETED]: { label: 'Produção Concluída', className: 'statusBadgeCompleted' },
    [ORDER_STATUS.READY_FOR_PICKING]: { label: 'Pronto para Separação', className: 'statusBadgeInfo' },
    [ORDER_STATUS.SHIPPED]: { label: 'Enviado', className: 'statusBadgeInfo' },
    [ORDER_STATUS.DELIVERED]: { label: 'Entregue', className: 'statusBadgeCompleted' },
    [ORDER_STATUS.CANCELLED]: { label: 'Cancelado', className: 'statusBadgeCancelled' }
};

// Helper functions - USD Native
const formatCurrencyUSD = (value) => {
    if (!value && value !== 0) return '$ 0.00';
    return `$ ${Number(value).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
};

// Keep BRL format for notifications and display where needed
const formatCurrency = (value) => {
    if (!value && value !== 0) return 'R$ 0,00';
    return `R$ ${Number(value).toLocaleString('pt-BR', { minimumFractionDigits: 2 })}`;
};

const formatDate = (dateValue) => {
    if (!dateValue) return '-';
    if (dateValue.toDate) return dateValue.toDate().toLocaleDateString('pt-BR');
    if (typeof dateValue === 'string') return new Date(dateValue).toLocaleDateString('pt-BR');
    if (dateValue instanceof Date) return dateValue.toLocaleDateString('pt-BR');
    return '-';
};

const getStatusClass = (status) => {
    const statusConfig = ORDER_STATUS_LABELS[status] || ORDER_STATUS_LABELS[ORDER_STATUS.PENDING];
    return statusConfig.className;
};

const getStatusLabel = (status) => {
    const statusConfig = ORDER_STATUS_LABELS[status] || ORDER_STATUS_LABELS[ORDER_STATUS.PENDING];
    return statusConfig.label;
};

// Helper para determinar se o material é água
const isWaterMaterial = (material) => {
    if (!material) return false;
    const name = (material.name || material.inciName || material.commercialName || '').toUpperCase();
    return name.includes('ÁGUA') || name.includes('AGUA') || name === 'WATER';
};

// ============================================
// 2. STOCK CHECK MODAL
// ============================================
const StockCheckModal = ({ order, onClose, onUpdateToPicking, addToast }) => {
    const [stockInfo, setStockInfo] = useState([]);
    const [loading, setLoading] = useState(true);
    const [updating, setUpdating] = useState(false);
    const [stockEntryModal, setStockEntryModal] = useState(null);
    const [stockEntryProduct, setStockEntryProduct] = useState(null);
    const [stockEntryQuantity, setStockEntryQuantity] = useState(0);
    const [refreshing, setRefreshing] = useState(false);

    const fetchStockInfo = useCallback(async () => {
        if (!order?.items) return;

        setLoading(true);
        try {
            const productIds = order.items.map(item => item.productId).filter(id => id);

            if (productIds.length === 0) {
                setStockInfo([]);
                setLoading(false);
                return;
            }

            const stockMap = new Map();

            for (let i = 0; i < productIds.length; i += 10) {
                const batch = productIds.slice(i, i + 10);
                const currentStockRef = collection(db, CURRENT_STOCK_COLLECTION);
                const q = query(currentStockRef, where('rawMaterialId', 'in', batch));
                const stockSnapshot = await getDocs(q);

                stockSnapshot.forEach(doc => {
                    const data = doc.data();
                    const existing = stockMap.get(data.rawMaterialId) || 0;
                    stockMap.set(data.rawMaterialId, existing + (data.quantity || 0));
                });
            }

            const info = order.items.map(item => {
                const currentStock = stockMap.get(item.productId) || 0;
                const requestedQty = item.quantity || 0;
                const hasStock = currentStock >= requestedQty;
                const shortage = hasStock ? 0 : requestedQty - currentStock;
                const isSampleOrBonus = item.isSample || item.isBonus;

                return {
                    id: item.id,
                    productId: item.productId,
                    code: item.code,
                    name: item.name,
                    requestedQty,
                    currentStock: isSampleOrBonus ? 'N/A' : currentStock,
                    hasStock: isSampleOrBonus || hasStock,
                    shortage: isSampleOrBonus ? 0 : shortage,
                    unitPrice: item.unitPrice,
                    isSampleOrBonus
                };
            });

            setStockInfo(info);
        } catch (error) {
            console.error('Error fetching stock info:', error);
            if (addToast) addToast('Erro ao verificar estoque', 'Error');
        } finally {
            setLoading(false);
        }
    }, [order, addToast]);

    useEffect(() => {
        fetchStockInfo();
    }, [fetchStockInfo]);

    const handleOpenStockEntry = (item) => {
        const shortage = item.requestedQty - item.currentStock;
        setStockEntryProduct({
            id: item.productId,
            code: item.code,
            name: item.name,
            commercialName: item.name
        });
        setStockEntryQuantity(Math.max(0, shortage));
        setStockEntryModal(true);
    };

    const handleStockEntrySuccess = async () => {
        if (addToast) addToast('Estoque adicionado com sucesso! Verificando novamente...', 'Success');
        setRefreshing(true);
        await fetchStockInfo();
        setRefreshing(false);
    };

    if (!order) return null;

    const totalItems = stockInfo.length;
    const itemsWithStock = stockInfo.filter(i => i.hasStock).length;
    const itemsWithoutStock = totalItems - itemsWithStock;
    const allItemsAvailable = itemsWithoutStock === 0;

    const handleUpdateToPicking = async () => {
        if (!allItemsAvailable) {
            if (addToast) addToast('Não é possível enviar para separação pois há itens com estoque insuficiente.', 'Warning');
            return;
        }

        setUpdating(true);
        try {
            await onUpdateToPicking(order.id);
            onClose();
        } catch (error) {
            console.error('Error updating to picking:', error);
            if (addToast) addToast('Erro ao atualizar status. Tente novamente.', 'Error');
        } finally {
            setUpdating(false);
        }
    };

    const isOutOfStock = (item) => {
        return !item.isSampleOrBonus && !item.hasStock;
    };

    return (
        <div className={styles.modalBackdrop} onClick={onClose}>
            <div className={`${styles.formContainer} ${styles.formContainerMedium}`} onClick={(e) => e.stopPropagation()}>
                <div className={styles.modalHeader}>
                    <div>
                        <h2><FiPackage size={24} style={{ marginRight: '10px' }} /> Verificar Estoque</h2>
                        <p className={styles.modalSubtitle}>
                            Pedido: <strong>{order.code}</strong> - {order.clientName}
                        </p>
                    </div>
                    <button type="button" onClick={onClose} className={styles.modalCloseButton} aria-label="Fechar">
                        <FiX size={24} />
                    </button>
                </div>

                <div className={styles.formContent}>
                    <div className={styles.approvalSummary}>
                        <div className={styles.approvalSummaryItem}>
                            <span className="count" style={{ color: itemsWithStock === totalItems ? 'var(--success-color)' : 'var(--warning-color)' }}>
                                {itemsWithStock}/{totalItems}
                            </span>
                            <span className="label">Itens com Estoque Disponível</span>
                        </div>
                        {itemsWithoutStock > 0 && (
                            <div className={styles.approvalSummaryItem}>
                                <span className="count" style={{ color: 'var(--danger-color)' }}>{itemsWithoutStock}</span>
                                <span className="label">Itens sem Estoque Suficiente</span>
                            </div>
                        )}
                    </div>

                    {refreshing && (
                        <div className={styles.infoBox} style={{ backgroundColor: 'var(--info-light)', borderLeftColor: 'var(--info-color)' }}>
                            <FiRefreshCw size={20} className={styles.spinner} />
                            <span>Atualizando informações de estoque...</span>
                        </div>
                    )}

                    <div className={styles.formSection}>
                        <div className={styles.sectionHeader}>
                            <div className={styles.sectionIcon}><FiTrendingUp size={18} /></div>
                            <h3 className={styles.sectionTitle}>Verificação de Estoque por Produto</h3>
                            {itemsWithoutStock > 0 && (
                                <span className={styles.sectionBadge} style={{ background: 'var(--danger-light)', color: 'var(--danger-color)' }}>
                                    {itemsWithoutStock} com falta
                                </span>
                            )}
                        </div>
                        <div className={styles.tableContainer}>
                            <table className={styles.table}>
                                <thead>
                                    <tr>
                                        <th>Código</th>
                                        <th>Produto</th>
                                        <th className="text-right">Solicitado</th>
                                        <th className="text-right">Estoque Atual</th>
                                        <th>Status</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {loading ? (
                                        <tr><td colSpan="5" className={styles.emptyTable}>Carregando informações de estoque...</td></tr>
                                    ) : stockInfo.length === 0 ? (
                                        <tr><td colSpan="5" className={styles.emptyTable}>Nenhum produto para verificar</td></tr>
                                    ) : (
                                        stockInfo.map(item => (
                                            <tr key={item.id} className={isOutOfStock(item) ? styles.bonusRow : ''}>
                                                <td>{item.code}</td>
                                                <td>{item.name}</td>
                                                <td className="text-right">{item.requestedQty.toFixed(3)}</td>
                                                <td className="text-right">
                                                    {item.isSampleOrBonus ? 'N/A' : item.currentStock.toLocaleString('pt-BR', { minimumFractionDigits: 3 })}
                                                </td>
                                                <td>
                                                    {item.hasStock ? (
                                                        <span className={`${styles.statusBadge} ${styles.statusBadgeCompleted}`}>
                                                            <FiCheck size={12} style={{ marginRight: '4px' }} />
                                                            {item.isSampleOrBonus ? 'Amostra/Bônus' : 'Disponível'}
                                                        </span>
                                                    ) : (
                                                        <div style={{ display: 'flex', flexDirection: 'column', gap: '4px', alignItems: 'flex-start' }}>
                                                            <span className={`${styles.statusBadge} ${styles.statusBadgeCancelled}`}>
                                                                <FiAlertCircle size={12} style={{ marginRight: '4px' }} />
                                                                Falta {item.shortage.toFixed(3)} un.
                                                            </span>
                                                            {!item.isSampleOrBonus && (
                                                                <button
                                                                    onClick={() => handleOpenStockEntry(item)}
                                                                    className={styles.addStockButton}
                                                                    style={{
                                                                        padding: '4px 10px',
                                                                        fontSize: '11px',
                                                                        borderRadius: '4px',
                                                                        border: 'none',
                                                                        background: 'var(--primary-color)',
                                                                        color: 'white',
                                                                        cursor: 'pointer',
                                                                        display: 'flex',
                                                                        alignItems: 'center',
                                                                        gap: '4px',
                                                                        transition: 'var(--transition-default)'
                                                                    }}
                                                                    onMouseEnter={(e) => e.currentTarget.style.background = 'var(--primary-color-dark)'}
                                                                    onMouseLeave={(e) => e.currentTarget.style.background = 'var(--primary-color)'}
                                                                    disabled={refreshing}
                                                                >
                                                                    <FiPackage size={12} /> Adicionar Estoque
                                                                </button>
                                                            )}
                                                        </div>
                                                    )}
                                                </td>
                                            </tr>
                                        ))
                                    )}
                                </tbody>
                            </table>
                        </div>
                    </div>

                    {itemsWithoutStock > 0 && (
                        <div className={styles.infoBox} style={{ backgroundColor: 'var(--danger-light)', borderLeftColor: 'var(--danger-color)' }}>
                            <FiAlertCircle size={20} />
                            <span>
                                <strong>Atenção:</strong> Existem {itemsWithoutStock} produto(s) com estoque insuficiente.
                                Clique em <strong>"Adicionar Estoque"</strong> para cada item para registrar a entrada manualmente.
                            </span>
                        </div>
                    )}

                    {allItemsAvailable && (
                        <div className={styles.infoBox} style={{ backgroundColor: 'var(--success-light)', borderLeftColor: 'var(--success-color)' }}>
                            <FiCheck size={20} />
                            <span>
                                <strong>Todos os produtos disponíveis em estoque!</strong>
                                Clique em "Enviar para Separação" para atualizar o status do pedido.
                            </span>
                        </div>
                    )}
                </div>

                <div className={styles.formActions}>
                    <button type="button" onClick={onClose} className={styles.secondaryButton} disabled={updating || refreshing}>
                        <FiX size={18} /> Fechar
                    </button>
                    {allItemsAvailable && (
                        <button
                            type="button"
                            onClick={handleUpdateToPicking}
                            className={styles.confirmButton}
                            disabled={updating || refreshing}
                        >
                            {updating ? (
                                <><FiRefreshCw size={18} className={styles.buttonSpinner} /> Processando...</>
                            ) : (
                                <><FiPackage size={18} /> Enviar para Separação</>
                            )}
                        </button>
                    )}
                </div>
            </div>

            {stockEntryModal && (
                <StockEntryModal
                    isOpen={stockEntryModal}
                    onClose={() => {
                        setStockEntryModal(false);
                        setStockEntryProduct(null);
                        setStockEntryQuantity(0);
                    }}
                    product={stockEntryProduct}
                    requestedQuantity={stockEntryQuantity}
                    onSuccess={handleStockEntrySuccess}
                    addToast={addToast}
                />
            )}
        </div>
    );
};

// ============================================
// 3. RETURN ORDER MODAL
// ============================================
const ReturnOrderModal = ({ order, onConfirm, onClose, addToast }) => {
    const [loading, setLoading] = useState(false);
    const [returnReason, setReturnReason] = useState('');

    if (!order) return null;

    const handleConfirm = async () => {
        if (!returnReason.trim()) {
            addToast('Por favor, informe o motivo do retorno.', 'Warning');
            return;
        }
        setLoading(true);
        try {
            await onConfirm(order.id, returnReason);
        } finally {
            setLoading(false);
        }
    };

    return (
        <div className={styles.modalBackdrop} onClick={onClose}>
            <div className={`${styles.formContainer} ${styles.formContainerSmall}`} onClick={(e) => e.stopPropagation()}>
                <div className={styles.modalHeader}>
                    <div>
                        <h2><FiArrowLeft size={24} style={{ marginRight: '10px', color: '#ff9800' }} /> Retornar Pedido</h2>
                        <p className={styles.modalSubtitle}>
                            Pedido: <strong>{order.code}</strong> - {order.clientName}
                        </p>
                    </div>
                    <button type="button" onClick={onClose} className={styles.modalCloseButton} disabled={loading} aria-label="Fechar">
                        <FiX size={24} />
                    </button>
                </div>

                <div className={styles.formContent}>
                    <div className={styles.infoBox} style={{ backgroundColor: 'var(--warning-light)', borderLeftColor: 'var(--warning-color)' }}>
                        <FiArrowLeft size={20} />
                        <span>
                            Ao retornar este pedido, ele voltará para a lista de pedidos pendentes no módulo de Pedidos de Venda,
                            onde poderá ser editado novamente antes de uma nova aprovação.
                        </span>
                    </div>

                    <div className={styles.formSection}>
                        <div className={styles.sectionHeader}>
                            <div className={styles.sectionIcon}><FiClipboard size={18} /></div>
                            <h3 className={styles.sectionTitle}>Motivo do Retorno</h3>
                        </div>
                        <div style={{ padding: '20px' }}>
                            <textarea
                                value={returnReason}
                                onChange={(e) => setReturnReason(e.target.value)}
                                className={styles.textarea}
                                rows={4}
                                placeholder="Informe o motivo pelo qual o pedido está sendo retornado para edição..."
                                disabled={loading}
                            />
                            <small className={styles.fieldHint}>* Campo obrigatório</small>
                        </div>
                    </div>

                    <div className={styles.orderSummary}>
                        <div className={styles.orderSummaryTitle}>Detalhes do Pedido</div>
                        <div className={styles.orderSummaryGrid}>
                            <div className={styles.orderSummaryItem}>
                                <span className={styles.orderSummaryLabel}>Cliente:</span>
                                <span className={styles.orderSummaryValue}>{order.clientName}</span>
                            </div>
                            <div className={styles.orderSummaryItem}>
                                <span className={styles.orderSummaryLabel}>Valor Total:</span>
                                <span className={styles.orderSummaryValue}>{formatCurrencyUSD(order.calculatedValues?.total)}</span>
                            </div>
                            <div className={styles.orderSummaryItem}>
                                <span className={styles.orderSummaryLabel}>Itens:</span>
                                <span className={styles.orderSummaryValue}>{order.items?.length || 0}</span>
                            </div>
                        </div>
                    </div>
                </div>

                <div className={styles.formActions}>
                    <button type="button" onClick={onClose} className={styles.secondaryButton} disabled={loading}>
                        <FiX size={18} /> Cancelar
                    </button>
                    <button type="button" onClick={handleConfirm} className={styles.warningButton} disabled={loading}>
                        {loading ? (
                            <><FiRefreshCw size={18} className={styles.buttonSpinner} /> Processando...</>
                        ) : (
                            <><FiArrowLeft size={18} /> Confirmar Retorno</>
                        )}
                    </button>
                </div>
            </div>
        </div>
    );
};

// ============================================
// 4. RELEASE ORDER MODAL
// ============================================
const ReleaseOrderModal = ({ order, onConfirm, onClose }) => {
    const [loading, setLoading] = useState(false);
    const [releaseNotes, setReleaseNotes] = useState('');

    if (!order) return null;

    const handleConfirm = async () => {
        setLoading(true);
        try {
            await onConfirm(order.id, releaseNotes);
        } finally {
            setLoading(false);
        }
    };

    return (
        <div className={styles.modalBackdrop} onClick={onClose}>
            <div className={`${styles.formContainer} ${styles.formContainerSmall}`} onClick={(e) => e.stopPropagation()}>
                <div className={styles.modalHeader}>
                    <div>
                        <h2><FiCheckCircle size={24} style={{ marginRight: '10px', color: '#28a745' }} /> Aprovar Pedido de Venda</h2>
                        <p className={styles.modalSubtitle}>
                            Pedido: <strong>{order.code}</strong> - {order.clientName}
                        </p>
                    </div>
                    <button type="button" onClick={onClose} className={styles.modalCloseButton} disabled={loading} aria-label="Fechar">
                        <FiX size={24} />
                    </button>
                </div>

                <div className={styles.formContent}>
                    <div className={styles.infoBox} style={{ backgroundColor: 'var(--success-light)', borderLeftColor: 'var(--success-color)' }}>
                        <FiCheck size={20} />
                        <span>
                            Ao aprovar este pedido, ele será encaminhado para o próximo módulo do fluxo e <strong>sairá da lista de aprovação</strong>.
                        </span>
                    </div>

                    <div className={styles.formSection}>
                        <div className={styles.sectionHeader}>
                            <div className={styles.sectionIcon}><FiClipboard size={18} /></div>
                            <h3 className={styles.sectionTitle}>Observações da Aprovação</h3>
                        </div>
                        <div style={{ padding: '20px' }}>
                            <textarea
                                value={releaseNotes}
                                onChange={(e) => setReleaseNotes(e.target.value)}
                                className={styles.textarea}
                                rows={4}
                                placeholder="Adicione observações sobre esta aprovação (opcional)..."
                                disabled={loading}
                            />
                        </div>
                    </div>

                    <div className={styles.orderSummary}>
                        <div className={styles.orderSummaryTitle}>Resumo do Pedido</div>
                        <div className={styles.orderSummaryGrid}>
                            <div className={styles.orderSummaryItem}>
                                <span className={styles.orderSummaryLabel}>Cliente:</span>
                                <span className={styles.orderSummaryValue}>{order.clientName}</span>
                            </div>
                            <div className={styles.orderSummaryItem}>
                                <span className={styles.orderSummaryLabel}>Valor Total:</span>
                                <span className={styles.orderSummaryValue}>{formatCurrencyUSD(order.calculatedValues?.total)}</span>
                            </div>
                            <div className={styles.orderSummaryItem}>
                                <span className={styles.orderSummaryLabel}>Itens:</span>
                                <span className={styles.orderSummaryValue}>{order.items?.length || 0}</span>
                            </div>
                        </div>
                    </div>
                </div>

                <div className={styles.formActions}>
                    <button type="button" onClick={onClose} className={styles.secondaryButton} disabled={loading}>
                        <FiX size={18} /> Cancelar
                    </button>
                    <button type="button" onClick={handleConfirm} className={styles.confirmButton} disabled={loading}>
                        {loading ? (
                            <><FiRefreshCw size={18} className={styles.buttonSpinner} /> Aprovando...</>
                        ) : (
                            <><FiCheck size={18} /> Confirmar Aprovação</>
                        )}
                    </button>
                </div>
            </div>
        </div>
    );
};

// ============================================
// 5. PRINT FUNCTIONS
// ============================================

const printFolhaPicking = async (order, addToast) => {
    if (!order) return;

    try {
        const productIds = order.items
            .map(item => item.productId)
            .filter(id => id);

        const lotesMap = new Map();

        if (productIds.length > 0) {
            for (let i = 0; i < productIds.length; i += 10) {
                const batch = productIds.slice(i, i + 10);
                const currentStockRef = collection(db, CURRENT_STOCK_COLLECTION);
                const q = query(currentStockRef, where('rawMaterialId', 'in', batch));
                const stockSnapshot = await getDocs(q);

                stockSnapshot.forEach(doc => {
                    const data = doc.data();
                    const rawMaterialId = data.rawMaterialId;

                    if (!lotesMap.has(rawMaterialId)) {
                        lotesMap.set(rawMaterialId, []);
                    }

                    const lotNumber = data.lotNumber || data.lote || null;
                    const quantity = data.quantity || 0;

                    if (lotNumber && quantity > 0.001) {
                        const existingLotes = lotesMap.get(rawMaterialId);
                        const existingLote = existingLotes.find(l => l.lote === lotNumber);

                        if (existingLote) {
                            existingLote.quantity += quantity;
                        } else {
                            lotesMap.get(rawMaterialId).push({
                                lote: lotNumber,
                                quantity: quantity,
                                validade: data.expirationDate || data.validade || null,
                                location: data.locationId || data.location || '',
                                locationDescription: data.locationDescription || ''
                            });
                        }
                    }
                });
            }
        }

        // Buscar dados completos do cliente
        let clientData = null;
        if (order.clientId) {
            try {
                const clientRef = doc(db, 'clients', order.clientId);
                const clientSnap = await getDoc(clientRef);
                if (clientSnap.exists()) {
                    clientData = clientSnap.data();
                }
            } catch (err) {
                console.warn('Erro ao buscar dados do cliente:', err);
            }
        }

        const itemsWithLotes = order.items.map(item => {
            const productLotes = lotesMap.get(item.productId) || [];
            const requestedQty = item.quantity || 0;
            const isSampleOrBonus = item.isSample || item.isBonus;

            const totalStock = productLotes.reduce((sum, lote) => sum + (lote.quantity || 0), 0);
            const hasStock = totalStock >= requestedQty;

            return {
                ...item,
                lotes: productLotes,
                totalStock: isSampleOrBonus ? 'N/A' : totalStock,
                hasStock: isSampleOrBonus || hasStock,
                isSampleOrBonus,
                hasLotes: productLotes.length > 0
            };
        });

        const printWindow = window.open('', '_blank');

        const formatDatePrint = (dateValue) => {
            if (!dateValue) return '-';
            if (dateValue.toDate) return dateValue.toDate().toLocaleDateString('pt-BR');
            if (typeof dateValue === 'string') return new Date(dateValue).toLocaleDateString('pt-BR');
            if (dateValue instanceof Date) return dateValue.toLocaleDateString('pt-BR');
            return '-';
        };

        const formatPhone = (phone) => {
            if (!phone) return '-';
            const cleaned = phone.replace(/\D/g, '');
            if (cleaned.length === 11) {
                return `(${cleaned.slice(0, 2)}) ${cleaned.slice(2, 7)}-${cleaned.slice(7)}`;
            }
            if (cleaned.length === 10) {
                return `(${cleaned.slice(0, 2)}) ${cleaned.slice(2, 6)}-${cleaned.slice(6)}`;
            }
            return phone;
        };

        // Extrair contatos dos endereços do cliente
        let clientContacts = {
            email: order.userEmail || order.contactEmail || '',
            phone: order.contactPhone || '',
            name: order.contactName || '',
            document: '',
            address: order.deliveryAddress || order.destination || '',
            city: '',
            state: '',
            zipCode: '',
            country: ''
        };

        if (clientData) {
            clientContacts.document = clientData.cnpj || clientData.cpf || clientData.document || '';

            // Pegar nome do contato principal
            if (clientData.contacts && clientData.contacts.length > 0) {
                const primaryContact = clientData.contacts[0];
                if (primaryContact) {
                    if (!clientContacts.name) clientContacts.name = primaryContact.nome || primaryContact.name || '';
                    if (!clientContacts.email) clientContacts.email = primaryContact.email || '';
                    if (!clientContacts.phone) clientContacts.phone = primaryContact.telefone || primaryContact.phone || '';
                }
            }

            // Pegar endereço principal
            if (clientData.addresses && clientData.addresses.length > 0) {
                const primaryAddr = clientData.addresses.find(a => a.isMain !== false) || clientData.addresses[0];
                if (primaryAddr) {
                    const addrParts = [
                        primaryAddr.logradouro || '',
                        primaryAddr.numero || '',
                        primaryAddr.complemento || '',
                        primaryAddr.bairro || '',
                        primaryAddr.cidade || '',
                        primaryAddr.uf || '',
                        primaryAddr.cep || ''
                    ].filter(Boolean);
                    if (addrParts.length > 0) {
                        clientContacts.address = addrParts.join(', ');
                    }
                    clientContacts.city = primaryAddr.cidade || '';
                    clientContacts.state = primaryAddr.uf || '';
                    clientContacts.zipCode = primaryAddr.cep || '';
                    clientContacts.country = primaryAddr.pais || clientData.pais || 'BRAZIL';
                }
            }
        }

        // Se ainda não tiver email, tentar do pedido
        if (!clientContacts.email) {
            clientContacts.email = order.userEmail || order.contactEmail || order.email || '';
        }

        const hasObservations = order.commercialNotes || order.productionNotes;
        const hasIssues = itemsWithLotes.some(i => !i.hasStock && !i.isSampleOrBonus);
        const totalItems = itemsWithLotes.length;
        const availableItems = itemsWithLotes.filter(i => i.hasStock).length;
        const outOfStockItems = itemsWithLotes.filter(i => !i.hasStock && !i.isSampleOrBonus).length;
        const bonusItems = itemsWithLotes.filter(i => i.isSampleOrBonus).length;

        const printContent = `
            <!DOCTYPE html>
            <html>
            <head>
                <title>Folha de Picking - ${order.code}</title>
                <meta charset="UTF-8">
                <style>
                    @page {
                        size: A4 portrait;
                        margin: 6mm 8mm 8mm 8mm;
                    }
                    * { 
                        margin: 0; 
                        padding: 0; 
                        box-sizing: border-box; 
                        -webkit-print-color-adjust: exact !important;
                        print-color-adjust: exact !important;
                    }
                    html, body {
                        width: 100%;
                        height: 100%;
                        background: #ffffff !important;
                        font-family: 'Segoe UI', 'Helvetica Neue', Arial, sans-serif;
                        font-size: 9px;
                        color: #1a1a2e;
                    }
                    .print-document {
                        width: 100%;
                        height: 100%;
                        min-height: 100%;
                        display: flex;
                        flex-direction: column;
                        padding: 0;
                        background: #ffffff;
                    }
                    .page-content {
                        flex: 1;
                        display: flex;
                        flex-direction: column;
                        justify-content: space-between;
                        height: 100%;
                    }
                    .content-top {
                        flex: 1;
                    }
                    .content-bottom {
                        flex-shrink: 0;
                    }

                    /* === HEADER === */
                    .header {
                        display: flex;
                        justify-content: space-between;
                        align-items: center;
                        border-bottom: 3px solid #1a237e;
                        padding-bottom: 5px;
                        margin-bottom: 6px;
                    }
                    .header-left {
                        display: flex;
                        align-items: center;
                        gap: 10px;
                    }
                    .logo-box {
                        width: 110px;
                        height: 42px;
                        display: flex;
                        align-items: center;
                        justify-content: center;
                        border: 1.5px solid #1a237e;
                        border-radius: 6px;
                        background: #ffffff;
                        overflow: hidden;
                        padding: 2px;
                    }
                    .logo-box img {
                        max-width: 100%;
                        max-height: 100%;
                        object-fit: contain;
                    }
                    .logo-box .logo-fallback {
                        font-size: 10px;
                        font-weight: 800;
                        color: #1a237e;
                        letter-spacing: 1px;
                    }
                    .header-center {
                        text-align: center;
                    }
                    .header-center h1 {
                        font-size: 20px;
                        font-weight: 800;
                        color: #1a237e;
                        letter-spacing: 3px;
                        text-transform: uppercase;
                        margin: 0;
                        line-height: 1.2;
                    }
                    .header-center .order-ref {
                        font-size: 14px;
                        font-weight: 700;
                        color: #0d1445;
                        letter-spacing: 1px;
                    }
                    .header-center .date-info {
                        font-size: 7.5px;
                        color: #7a7a9a;
                        letter-spacing: 0.3px;
                    }
                    .header-right {
                        text-align: right;
                        font-size: 7.5px;
                        color: #7a7a9a;
                        line-height: 1.6;
                    }
                    .header-right strong {
                        color: #1a237e;
                    }
                    .header-right .status-badge {
                        display: inline-block;
                        padding: 1px 10px;
                        border-radius: 10px;
                        background: #e8eaf6;
                        color: #1a237e;
                        font-weight: 700;
                        font-size: 7.5px;
                    }

                    /* === INFO CARD === */
                    .info-grid {
                        display: grid;
                        grid-template-columns: 1fr 1fr;
                        gap: 1px 16px;
                        background: #f8f9ff;
                        border-radius: 6px;
                        padding: 5px 12px;
                        margin-bottom: 6px;
                        border: 1px solid #e8eaf6;
                    }
                    .info-item {
                        display: flex;
                        align-items: center;
                        gap: 3px;
                        padding: 1.5px 0;
                        font-size: 8px;
                    }
                    .info-item .label {
                        font-weight: 600;
                        color: #5c5c7a;
                        font-size: 7px;
                        text-transform: uppercase;
                        letter-spacing: 0.3px;
                        min-width: 62px;
                    }
                    .info-item .value {
                        color: #1a1a2e;
                        font-weight: 500;
                        font-size: 8px;
                    }

                    /* === CONTACT CARD === */
                    .contact-grid {
                        display: grid;
                        grid-template-columns: 1fr 1fr 1fr;
                        gap: 2px 12px;
                        background: #f0f2fa;
                        border-radius: 6px;
                        padding: 4px 12px;
                        margin-bottom: 6px;
                        border: 1px solid #e8eaf6;
                    }
                    .contact-item {
                        display: flex;
                        align-items: center;
                        gap: 3px;
                        padding: 1.5px 0;
                        font-size: 7.5px;
                    }
                    .contact-item .label {
                        font-weight: 600;
                        color: #5c5c7a;
                        font-size: 7px;
                        text-transform: uppercase;
                        letter-spacing: 0.3px;
                        min-width: 40px;
                    }
                    .contact-item .value {
                        color: #1a1a2e;
                        font-weight: 500;
                        font-size: 7.5px;
                    }

                    /* === STATS === */
                    .stats-grid {
                        display: grid;
                        grid-template-columns: repeat(4, 1fr);
                        gap: 5px;
                        margin-bottom: 6px;
                    }
                    .stat-card {
                        background: linear-gradient(135deg, #1a237e, #283593);
                        border-radius: 6px;
                        padding: 4px 6px;
                        text-align: center;
                        color: white;
                    }
                    .stat-card .number {
                        font-size: 18px;
                        font-weight: 800;
                        display: block;
                        line-height: 1.2;
                    }
                    .stat-card .number.green { color: #81c784; }
                    .stat-card .number.red { color: #ef9a9a; }
                    .stat-card .number.orange { color: #ffcc80; }
                    .stat-card .number.white { color: #ffffff; }
                    .stat-card .label {
                        font-size: 6.5px;
                        text-transform: uppercase;
                        letter-spacing: 0.5px;
                        opacity: 0.85;
                    }

                    /* === TABLE === */
                    .table-wrapper {
                        border-radius: 6px;
                        overflow: hidden;
                        border: 1px solid #e8eaf6;
                        flex: 1;
                        margin-bottom: 4px;
                    }
                    table {
                        width: 100%;
                        border-collapse: collapse;
                        font-size: 8px;
                    }
                    thead {
                        display: table-header-group;
                    }
                    th {
                        background: linear-gradient(135deg, #e8eaf6, #d5d9eb);
                        color: #1a237e;
                        font-weight: 700;
                        font-size: 7px;
                        text-transform: uppercase;
                        letter-spacing: 0.4px;
                        padding: 4px 6px;
                        text-align: center;
                        border-bottom: 2px solid #1a237e;
                    }
                    td {
                        padding: 3px 6px;
                        border-bottom: 1px solid #f0f2fa;
                        vertical-align: middle;
                    }
                    tr:last-child td {
                        border-bottom: none;
                    }
                    
                    .col-idx { width: 22px; text-align: center; font-weight: 600; color: #7a7a9a; font-size: 7.5px; }
                    .col-code { width: 50px; font-weight: 600; color: #1a237e; font-size: 7.5px; }
                    .col-product { min-width: 70px; font-size: 7.5px; }
                    .col-qty { width: 35px; text-align: center; font-weight: 600; font-size: 8px; }
                    .col-lotes { min-width: 80px; font-size: 7.5px; }
                    .col-status { width: 65px; text-align: center; font-size: 7.5px; }

                    .row-bonus { background: #fff8e1; }
                    .row-sample { background: #e3f2fd; }
                    .row-warning { background: #ffebee; }
                    .row-normal { background: #ffffff; }

                    .badge {
                        display: inline-block;
                        padding: 0px 6px;
                        border-radius: 8px;
                        font-size: 6px;
                        font-weight: 700;
                        text-transform: uppercase;
                        letter-spacing: 0.3px;
                        margin-left: 3px;
                    }
                    .badge-bonus { background: #ff9800; color: #fff; }
                    .badge-sample { background: #1976d2; color: #fff; }

                    .lote-tag {
                        display: inline-block;
                        padding: 0px 6px;
                        border-radius: 8px;
                        background: #e3f2fd;
                        border: 1px solid #90caf9;
                        font-size: 7px;
                        font-weight: 600;
                        color: #0d47a1;
                        margin: 1px 2px 1px 0;
                    }
                    .lote-tag-empty {
                        display: inline-block;
                        padding: 0px 6px;
                        border-radius: 8px;
                        background: #ffebee;
                        border: 1px solid #ef9a9a;
                        font-size: 6.5px;
                        color: #c62828;
                        font-weight: 600;
                    }

                    .status-avail { color: #2e7d32; font-weight: 700; }
                    .status-falta { color: #c62828; font-weight: 700; }
                    .status-na { color: #7a7a9a; font-style: italic; font-size: 7px; }

                    /* === OBSERVATIONS === */
                    .obs-box {
                        background: #fff8e1;
                        border-left: 3px solid #ffa000;
                        border-radius: 4px;
                        padding: 2px 10px;
                        margin-bottom: 4px;
                        font-size: 7.5px;
                        color: #4a3000;
                    }
                    .obs-box strong { color: #e65100; }

                    /* === FOOTER === */
                    .footer-signatures {
                        display: grid;
                        grid-template-columns: 1fr 1fr 1fr;
                        gap: 10px;
                        padding-top: 5px;
                        border-top: 2px solid #e8eaf6;
                        margin-top: 3px;
                    }
                    .sig-box {
                        text-align: center;
                        padding: 1px 0;
                    }
                    .sig-box .sig-title {
                        font-weight: 700;
                        font-size: 7.5px;
                        color: #1a237e;
                        text-transform: uppercase;
                        letter-spacing: 0.5px;
                    }
                    .sig-box .sig-line {
                        border-top: 1.5px solid #1a1a2e;
                        margin: 2px auto 1px;
                        width: 75%;
                    }
                    .sig-box .sig-sub {
                        font-size: 6.5px;
                        color: #7a7a9a;
                    }
                    .sig-box .sig-check {
                        font-size: 7px;
                        color: #1a1a2e;
                        margin-top: 1px;
                    }
                    .sig-box .sig-check span {
                        margin: 0 2px;
                    }

                    .footer-bar {
                        display: flex;
                        justify-content: space-between;
                        align-items: center;
                        margin-top: 3px;
                        padding-top: 3px;
                        border-top: 1px solid #f0f2fa;
                        font-size: 6.5px;
                        color: #7a7a9a;
                    }
                    .footer-bar .alert {
                        font-weight: 700;
                    }
                    .footer-bar .alert.danger { color: #c62828; }
                    .footer-bar .alert.success { color: #2e7d32; }

                    .no-print { display: none !important; }

                    @media print {
                        html, body {
                            height: 100%;
                            margin: 0;
                            padding: 0;
                        }
                        .print-document {
                            height: 100%;
                            min-height: 100%;
                        }
                        .page-content {
                            height: 100%;
                            min-height: 100%;
                        }
                        .no-print { display: none !important; }
                        .stat-card { -webkit-print-color-adjust: exact; print-color-adjust: exact; }
                        th { -webkit-print-color-adjust: exact; print-color-adjust: exact; }
                        .row-bonus, .row-sample, .row-warning { -webkit-print-color-adjust: exact; print-color-adjust: exact; }
                        .info-grid, .contact-grid { -webkit-print-color-adjust: exact; print-color-adjust: exact; }
                        .logo-box img { -webkit-print-color-adjust: exact; print-color-adjust: exact; }
                    }
                </style>
            </head>
            <body>
                <div class="print-document">
                    <div class="page-content">
                        <!-- TOP CONTENT -->
                        <div class="content-top">
                            <!-- HEADER -->
                            <div class="header">
                                <div class="header-left">
                                    <div class="logo-box">
                                        <img src="/assets/grupo_logo.png" alt="Grupo B2E" onerror="this.style.display='none';this.parentElement.innerHTML='<span class=\\'logo-fallback\\'>✦ GRUPO B2E</span>';" />
                                    </div>
                                    <div style="font-size:7.5px; font-weight:600; color:#1a237e; line-height:1.3;">
                                        PICKING<br>
                                        <span style="font-weight:400; color:#7a7a9a; font-size:6.5px;">LIST</span>
                                    </div>
                                </div>
                                <div class="header-center">
                                    <div class="order-ref">${order.code}</div>
                                    <div class="date-info">${new Date().toLocaleDateString('pt-BR')} • ${new Date().toLocaleTimeString('pt-BR', { hour: '2-digit', minute: '2-digit' })}</div>
                                </div>
                                <div class="header-right">
                                    <div><strong>Status</strong><br><span class="status-badge">${getStatusLabel(order.status)}</span></div>
                                    <div style="margin-top:1px;"><strong>Pág.</strong> 1/1</div>
                                </div>
                            </div>

                            <!-- CLIENT INFO -->
                            <div class="info-grid">
                                <div class="info-item"><span class="label">Cliente</span><span class="value">${clientData?.razaoSocial || clientData?.nomeFantasia || order.clientName || '-'}</span></div>
                                <div class="info-item"><span class="label">O.C.</span><span class="value">${order.clientOrderNumber || '-'}</span></div>
                                <div class="info-item"><span class="label">Documento</span><span class="value">${clientContacts.document || '-'}</span></div>
                                <div class="info-item"><span class="label">Entrega</span><span class="value">${formatDatePrint(order.deliveryDate)}</span></div>
                            </div>

                            <!-- CONTACT INFO -->
                            <div class="contact-grid">
                                <div class="contact-item"><span class="label">Contato</span><span class="value">${clientContacts.name || '-'}</span></div>
                                <div class="contact-item"><span class="label">Email</span><span class="value">${clientContacts.email || '-'}</span></div>
                                <div class="contact-item"><span class="label">Telefone</span><span class="value">${formatPhone(clientContacts.phone) || '-'}</span></div>
                                <div class="contact-item"><span class="label">Endereço</span><span class="value">${clientContacts.address || '-'}</span></div>
                                <div class="contact-item"><span class="label">Cidade/UF</span><span class="value">${clientContacts.city ? `${clientContacts.city}/${clientContacts.state}` : '-'}</span></div>
                                <div class="contact-item"><span class="label">País</span><span class="value">${clientContacts.country || 'BRAZIL'}</span></div>
                            </div>

                            <!-- STATS -->
                            <div class="stats-grid">
                                <div class="stat-card">
                                    <span class="number white">${totalItems}</span>
                                    <span class="label">Total Itens</span>
                                </div>
                                <div class="stat-card">
                                    <span class="number green">${availableItems}</span>
                                    <span class="label">Disponíveis</span>
                                </div>
                                <div class="stat-card">
                                    <span class="number red">${outOfStockItems}</span>
                                    <span class="label">Falta Estoque</span>
                                </div>
                                <div class="stat-card">
                                    <span class="number orange">${bonusItems}</span>
                                    <span class="label">Bônus/Amostras</span>
                                </div>
                            </div>

                            <!-- TABLE -->
                            <div class="table-wrapper">
                                <table>
                                    <thead>
                                        <tr>
                                            <th class="col-idx">#</th>
                                            <th class="col-code">Código</th>
                                            <th style="text-align:left; padding-left:6px;">Produto</th>
                                            <th class="col-qty">Qtde</th>
                                            <th style="text-align:left; padding-left:6px;">Lotes Disponíveis</th>
                                            <th class="col-status">Status</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        ${itemsWithLotes.map((item, idx) => {
            let lotesHtml = '';
            if (item.isSampleOrBonus) {
                lotesHtml = '<span class="status-na">—</span>';
            } else if (item.lotes && item.lotes.length > 0) {
                const displayLotes = item.lotes.slice(0, 4);
                lotesHtml = displayLotes.map(l =>
                    `<span class="lote-tag">${l.lote}</span>`
                ).join('');
                if (item.lotes.length > 4) {
                    lotesHtml += `<span style="font-size:6px;color:#7a7a9a;"> +${item.lotes.length - 4}</span>`;
                }
            } else {
                lotesHtml = '<span class="lote-tag-empty">Nenhum</span>';
            }

            let statusHtml = '';
            let statusClass = '';
            if (item.isSampleOrBonus) {
                statusHtml = 'N/A';
                statusClass = 'status-na';
            } else if (item.hasStock) {
                statusHtml = '✓ Disponível';
                statusClass = 'status-avail';
            } else {
                statusHtml = '✗ Falta';
                statusClass = 'status-falta';
            }

            let rowClass = 'row-normal';
            if (item.isBonus) rowClass = 'row-bonus';
            else if (item.isSample) rowClass = 'row-sample';
            else if (!item.hasStock && !item.isSampleOrBonus) rowClass = 'row-warning';

            const badges = [];
            if (item.isBonus) badges.push('<span class="badge badge-bonus">Bônus</span>');
            if (item.isSample) badges.push('<span class="badge badge-sample">Amostra</span>');

            return `
                                                <tr class="${rowClass}">
                                                    <td class="col-idx">${idx + 1}</td>
                                                    <td class="col-code">${item.code || '-'}</td>
                                                    <td style="text-align:left; padding-left:6px;">
                                                        ${item.name || '-'}
                                                        ${badges.join(' ')}
                                                    </td>
                                                    <td class="col-qty">${item.quantity || 0}</td>
                                                    <td style="text-align:left; padding-left:6px;">${lotesHtml}</td>
                                                    <td class="col-status"><span class="${statusClass}">${statusHtml}</span></td>
                                                </tr>
                                            `;
        }).join('')}
                                    </tbody>
                                </table>
                            </div>

                            <!-- OBSERVATIONS -->
                            ${hasObservations ? `
                                <div class="obs-box">
                                    ${order.commercialNotes ? `<div><strong>📝 Obs.:</strong> ${order.commercialNotes}</div>` : ''}
                                    ${order.productionNotes ? `<div style="margin-top:1px;"><strong>🏭 Produção:</strong> ${order.productionNotes}</div>` : ''}
                                </div>
                            ` : ''}
                        </div>

                        <!-- BOTTOM CONTENT -->
                        <div class="content-bottom">
                            <!-- SIGNATURES -->
                            <div class="footer-signatures">
                                <div class="sig-box">
                                    <div class="sig-title">Conferente</div>
                                    <div class="sig-line"></div>
                                    <div class="sig-sub">Nome: _________________________</div>
                                    <div class="sig-sub">Data: ___/___/______</div>
                                </div>
                                <div class="sig-box">
                                    <div class="sig-title">Separação</div>
                                    <div class="sig-line"></div>
                                    <div class="sig-sub">Início: _____:_____</div>
                                    <div class="sig-sub">Término: _____:_____</div>
                                </div>
                                <div class="sig-box">
                                    <div class="sig-title">Status Final</div>
                                    <div class="sig-line" style="border-color:transparent;"></div>
                                    <div class="sig-check">
                                        <span>☐ Completo</span>
                                        <span>☐ Parcial</span>
                                        <span>☐ Pendente</span>
                                    </div>
                                </div>
                            </div>

                            <!-- FOOTER BAR -->
                            <div class="footer-bar">
                                <span>Gerado em ${new Date().toLocaleString('pt-BR')}</span>
                                <span class="alert ${hasIssues ? 'danger' : 'success'}">
                                    ${hasIssues ? '⚠️ ATENÇÃO: Itens sem estoque disponível!' : '✅ Todos os itens disponíveis'}
                                </span>
                            </div>
                        </div>
                    </div>
                </div>

                <div class="no-print" style="text-align:center; margin-top:10px; padding:12px; background:#f5f5f5; border-radius:10px; display:flex; justify-content:center; gap:12px;">
                    <button onclick="window.print();" style="padding:10px 36px; cursor:pointer; background:linear-gradient(135deg, #1a237e, #283593); color:white; border:none; border-radius:6px; font-size:14px; font-weight:600; box-shadow:0 3px 10px rgba(26,35,126,0.25);">
                        🖨️ Imprimir
                    </button>
                    <button onclick="window.close();" style="padding:10px 36px; cursor:pointer; background:#e0e0e0; color:#333; border:none; border-radius:6px; font-size:14px; font-weight:600;">
                        ✖ Fechar
                    </button>
                </div>
            </body>
            </html>
        `;

        printWindow.document.write(printContent);
        printWindow.document.close();

    } catch (error) {
        console.error('Error generating Folha de Picking:', error);
        if (addToast) addToast(`Erro ao gerar Folha de Picking: ${error.message}`, 'Error');
    }
};

const printRomaneio = (order) => {
    if (!order) return;

    const printWindow = window.open('', '_blank');

    const formatCurrencyPrint = (value) => {
        if (!value && value !== 0) return '$ 0.00';
        return `$ ${Number(value).toLocaleString('en-US', { minimumFractionDigits: 2 })}`;
    };

    const formatDatePrint = (dateValue) => {
        if (!dateValue) return '-';
        if (dateValue.toDate) return dateValue.toDate().toLocaleDateString('pt-BR');
        if (typeof dateValue === 'string') return new Date(dateValue).toLocaleDateString('pt-BR');
        if (dateValue instanceof Date) return dateValue.toLocaleDateString('pt-BR');
        return '-';
    };

    const printContent = `
        <!DOCTYPE html>
        <html>
        <head>
            <title>Romaneio - ${order.code}</title>
            <meta charset="UTF-8">
            <style>
                * { margin: 0; padding: 0; box-sizing: border-box; }
                body { font-family: Arial, sans-serif; margin: 20px; padding: 20px; background: #fff; }
                .header { text-align: center; margin-bottom: 30px; border-bottom: 2px solid #333; padding-bottom: 10px; }
                .title { font-size: 24px; font-weight: bold; margin: 0; color: #333; }
                .subtitle { color: #666; margin: 5px 0; }
                .section { margin-bottom: 25px; page-break-inside: avoid; }
                .section-title { background: #f0f0f0; padding: 10px; font-weight: bold; margin-bottom: 15px; border-left: 4px solid #007bff; }
                .info-grid { display: grid; grid-template-columns: repeat(2, 1fr); gap: 12px; margin-bottom: 15px; }
                .info-item { margin-bottom: 8px; }
                .info-label { font-weight: bold; font-size: 12px; color: #666; display: block; margin-bottom: 4px; }
                .info-value { font-size: 14px; color: #333; }
                table { width: 100%; border-collapse: collapse; margin-bottom: 15px; }
                th, td { border: 1px solid #ddd; padding: 10px; text-align: left; }
                th { background: #f0f0f0; font-weight: bold; }
                .text-right { text-align: right; }
                .footer { margin-top: 30px; text-align: center; font-size: 12px; color: #666; border-top: 1px solid #ddd; padding-top: 10px; }
                .total-row { font-weight: bold; background: #f8f9fa; }
                @media print { body { margin: 0; padding: 10px; } .no-print { display: none; } }
            </style>
        </head>
        <body>
            <div class="header">
                <div class="title">ROMANEIO DE PEDIDO</div>
                <div class="subtitle">Pedido Nº ${order.code}</div>
                <div class="subtitle">Data: ${formatDatePrint(order.createdAt)}</div>
            </div>

            <div class="section">
                <div class="section-title">INFORMAÇÕES DO CLIENTE</div>
                <div class="info-grid">
                    <div class="info-item">
                        <div class="info-label">Cliente:</div>
                        <div class="info-value">${order.clientName || '-'}</div>
                    </div>
                    <div class="info-item">
                        <div class="info-label">Endereço de Entrega:</div>
                        <div class="info-value">${order.deliveryAddress || '-'}</div>
                    </div>
                    <div class="info-item">
                        <div class="info-label">Contato:</div>
                        <div class="info-value">${order.contactName || '-'} ${order.contactPhone ? `(${order.contactPhone})` : ''}</div>
                    </div>
                    <div class="info-item">
                        <div class="info-label">O.C. do Cliente:</div>
                        <div class="info-value">${order.clientOrderNumber || '-'}</div>
                    </div>
                </div>
            </div>

            <div class="section">
                <div class="section-title">PRODUTOS</div>
                <table>
                    <thead>
                        <tr><th>#</th><th>Código</th><th>Produto</th><th class="text-right">Qtde</th><th class="text-right">Preço Unit.</th><th class="text-right">Total</th></tr>
                    </thead>
                    <tbody>
                        ${order.items?.map((item, idx) => `
                            <tr>
                                <td class="text-right">${idx + 1}</td>
                                <td>${item.code || '-'}</td>
                                <td>${item.name || '-'}${item.isBonus ? ' (BÔNUS)' : ''}${item.isSample ? ' (AMOSTRA)' : ''}</td>
                                <td class="text-right">${item.quantity || 0}</td>
                                <td class="text-right">${formatCurrencyPrint(item.unitPrice)}</td>
                                <td class="text-right">${formatCurrencyPrint(item.total)}</td>
                            </tr>
                        `).join('') || '<tr><td colspan="6" class="text-right">Nenhum produto</td></tr>'}
                    </tbody>
                    <tfoot>
                        <tr class="total-row">
                            <td colspan="5" class="text-right"><strong>Total:</strong></td>
                            <td class="text-right"><strong>${formatCurrencyPrint(order.calculatedValues?.total)}</strong></td>
                        </tr>
                    </tfoot>
                </table>
            </div>

            <div class="section">
                <div class="section-title">OBSERVAÇÕES</div>
                <div class="info-value">${(order.productionNotes || order.commercialNotes) || 'Nenhuma observação registrada.'}</div>
            </div>

            <div class="footer">
                Documento gerado eletronicamente em ${new Date().toLocaleString('pt-BR')}
            </div>
            <div class="no-print" style="text-align: center; margin-top: 20px;">
                <button onclick="window.print();" style="padding: 10px 20px; margin: 0 5px; cursor: pointer;">🖨️ Imprimir</button>
                <button onclick="window.close();" style="padding: 10px 20px; margin: 0 5px; cursor: pointer;">✖ Fechar</button>
            </div>
        </body>
        </html>
    `;

    printWindow.document.write(printContent);
    printWindow.document.close();
};

// ============================================
// 6. SEND TO PRODUCTION FUNCTIONS
// ============================================

const fetchProductFormula = async (productId) => {
    try {
        const productRef = doc(db, PRODUCTS_COLLECTION, productId);
        const productSnap = await getDoc(productRef);

        if (!productSnap.exists()) {
            console.error(`Produto ${productId} não encontrado na coleção products`);
            return null;
        }

        const productData = productSnap.data();

        if (!productData.rawMaterials || productData.rawMaterials.length === 0) {
            console.error(`Produto ${productData.code} não possui fórmula cadastrada (rawMaterials vazio)`);
            return null;
        }

        return {
            rawMaterials: productData.rawMaterials || [],
            lossIndex: productData.lossIndex || 1,
            gramatura: productData.technical?.gramatura || productData.gramatura || 0,
            gramaturaUnit: productData.technical?.gramaturaUnit || 'g',
            code: productData.code,
            commercialName: productData.commercialName
        };
    } catch (error) {
        console.error('Erro ao buscar fórmula do produto:', error);
        return null;
    }
};

const fetchMaterialDetails = async (materialId) => {
    try {
        const productRef = doc(db, PRODUCTS_COLLECTION, materialId);
        const productSnap = await getDoc(productRef);

        if (productSnap.exists()) {
            const productData = productSnap.data();
            return {
                id: materialId,
                name: productData.commercialName || productData.inciName,
                inciName: productData.inciName,
                commercialName: productData.commercialName,
                categoryIds: productData.categoryIds || [],
                productTypeIds: productData.productTypeIds || [],
                isProduct: true
            };
        }

        const itemsRef = collection(db, ITEMS_COLLECTION);
        const q = query(itemsRef, where('id', '==', materialId));
        const querySnapshot = await getDocs(q);

        if (!querySnapshot.empty) {
            const itemData = querySnapshot.docs[0].data();
            return {
                id: materialId,
                name: itemData.name || itemData.inciName || itemData.commercialName,
                inciName: itemData.inciName,
                commercialName: itemData.commercialName,
                categoryIds: itemData.categoryIds || [],
                productTypeIds: itemData.productTypeIds || [],
                isProduct: false
            };
        }

        console.warn(`Material ${materialId} não encontrado`);
        return null;
    } catch (error) {
        console.error('Erro ao buscar detalhes do material:', error);
        return null;
    }
};

const calculateMaterialRequirements = async (orderItem, productFormula, categories = []) => {
    const quantity = parseFloat(orderItem.quantity) || 0;
    const lossIndex = parseFloat(productFormula?.lossIndex) || 1;
    const gramatura = parseFloat(productFormula?.gramatura) || 0;
    const gramaturaUnit = productFormula?.gramaturaUnit || 'kg';

    const gramaturaBase = (gramaturaUnit === 'g' || gramaturaUnit === 'ml')
        ? gramatura / 1000
        : gramatura;

    const totalMass = quantity * gramaturaBase;

    const rawMaterials = productFormula?.rawMaterials || [];
    const requirements = {
        massItems: [],
        unitItems: [],
        totalMass
    };

    const unitCategoryNames = ['EMBALAGEM', 'SERIGRAFIA', 'FRASCO', 'TAMPA', 'ROTULO', 'CAIXA', 'CARTUCHO'];

    for (const material of rawMaterials) {
        const materialId = material.id;
        const quantityInFormula = parseFloat(material.quantity) || 0;

        const materialDetails = await fetchMaterialDetails(materialId);
        const isWater = isWaterMaterial(materialDetails);

        const materialCategories = materialDetails?.categoryIds?.map(catId => {
            const cat = categories.find(c => c.id === catId);
            return cat?.name?.toUpperCase() || '';
        }) || [];

        const isPackaging = materialCategories.some(name =>
            unitCategoryNames.some(term => name.includes(term))
        );

        const materialName = materialDetails?.name ||
            materialDetails?.commercialName ||
            material.inciName ||
            material.name ||
            materialId;

        if (isPackaging) {
            const needed = quantity * quantityInFormula;
            requirements.unitItems.push({
                id: materialId,
                name: materialName,
                needed: needed,
                unit: 'Un',
                isWater,
                quantityInFormula
            });
        } else {
            const baseNeeded = totalMass * quantityInFormula / 100;
            const finalNeededWithLoss = baseNeeded * lossIndex;
            requirements.massItems.push({
                id: materialId,
                name: materialName,
                needed: finalNeededWithLoss,
                unit: 'Kg',
                isWater,
                quantityInFormula
            });
        }
    }

    return requirements;
};

const generateOpCode = async () => {
    return await runTransaction(db, async (transaction) => {
        const currentYear = new Date().getFullYear();
        const allOpsRef = collection(db, PRODUCTION_ORDERS_COLLECTION);
        const allOpsSnap = await getDocs(allOpsRef);

        const thisYearOps = allOpsSnap.docs.filter(d => {
            const code = d.data().opCode || '';
            return code.startsWith(`OP${currentYear}.`);
        });

        const nextNum = thisYearOps.length + 1;
        const opCode = `OP${currentYear}.${String(nextNum).padStart(5, '0')}`;

        const existingOrder = thisYearOps.find(o => o.data().opCode === opCode);
        if (existingOrder) {
            throw new Error(`OP code collision detected: ${opCode}`);
        }

        return opCode;
    });
};

const generateLote = async (productCode) => {
    try {
        const currentYear = new Date().getFullYear();
        const opsSnap = await getDocs(collection(db, PRODUCTION_ORDERS_COLLECTION));

        let maxNumber = 0;

        opsSnap.docs.forEach(d => {
            const orderData = d.data();
            (orderData.items || []).forEach(item => {
                if (item.productCode === productCode && item.lote) {
                    const regex = new RegExp(`${productCode}(\\d{2})${currentYear}`);
                    const match = item.lote.match(regex);
                    if (match) {
                        const num = parseInt(match[1], 10);
                        if (num > maxNumber) maxNumber = num;
                    }
                }
            });
        });

        const nextNumber = (maxNumber + 1).toString().padStart(2, '10');
        return `${productCode}${nextNumber}${currentYear}`;
    } catch (error) {
        console.error("Error generating lote:", error);
        const currentYear = new Date().getFullYear();
        return `${productCode}01${currentYear}`;
    }
};

const sendToProduction = async (order, user, addToast, updateDocument, refreshData, categories = []) => {
    if (!order || !order.items || order.items.length === 0) {
        addToast('Pedido não possui itens para enviar para produção.', 'Error');
        return false;
    }

    addToast(`Iniciando criação de ordem de produção para ${order.code}...`, 'Info');

    const createdOrders = [];
    const errors = [];

    for (const orderItem of order.items) {
        try {
            const productFormula = await fetchProductFormula(orderItem.productId);

            if (!productFormula) {
                throw new Error(`Produto ${orderItem.code || orderItem.name} não possui fórmula cadastrada.`);
            }

            if (!productFormula.rawMaterials || productFormula.rawMaterials.length === 0) {
                throw new Error(`Produto ${orderItem.code || orderItem.name} não possui matérias-primas cadastradas.`);
            }

            const requirements = await calculateMaterialRequirements(orderItem, productFormula, categories);
            const opCode = await generateOpCode();
            const lote = await generateLote(orderItem.code);

            const allMaterials = [...requirements.massItems, ...requirements.unitItems];
            const nonWaterMaterials = allMaterials.filter(m => !m.isWater);

            const initialStatus = PRODUCTION_STATUS.AWAITING_START;

            const orderData = {
                opCode,
                saleOrderId: order.id,
                saleOrderCode: order.code,
                clientName: order.clientName,
                items: [{
                    productId: orderItem.productId,
                    productCode: orderItem.code,
                    productName: orderItem.name,
                    quantity: parseFloat(orderItem.quantity) || 0,
                    mode: 'mass',
                    lote: lote,
                    obs: `Pedido de venda: ${order.code}`,
                    waterPct: 0,
                    etapa: ''
                }],
                materialsUsed: nonWaterMaterials.map(m => ({
                    id: m.id,
                    name: m.name,
                    needed: parseFloat(Number(m.needed).toFixed(4)),
                    unit: m.unit
                })),
                materialsRequired: allMaterials.map(m => ({
                    id: m.id,
                    name: m.name,
                    needed: parseFloat(Number(m.needed).toFixed(4)),
                    unit: m.unit,
                    isWater: m.isWater || false
                })),
                status: initialStatus,
                createdAt: serverTimestamp(),
                source: 'sale-order-approval',
                sourceOrderId: order.id,
                version: 1,
                productFormulaUsed: {
                    lossIndex: productFormula.lossIndex,
                    gramatura: productFormula.gramatura,
                    gramaturaUnit: productFormula.gramaturaUnit
                }
            };

            const docRef = await addDoc(collection(db, PRODUCTION_ORDERS_COLLECTION), orderData);

            createdOrders.push({
                id: docRef.id,
                opCode,
                productName: orderItem.name,
                status: initialStatus
            });

            addToast(`OP ${opCode} criada para ${orderItem.name}`, 'Success');

        } catch (itemError) {
            console.error(`Erro ao criar OP para ${orderItem.name}:`, itemError);
            errors.push(`${orderItem.name}: ${itemError.message}`);
            addToast(`Erro ao criar OP para ${orderItem.name}: ${itemError.message}`, 'Error');
        }
    }

    if (createdOrders.length > 0) {
        const existingHistory = order.trackingHistory || [];
        const newHistoryEntry = {
            date: new Date().toLocaleDateString('pt-BR'),
            status: 'Em Produção',
            description: `Ordem(ns) de produção criada(s): ${createdOrders.map(o => o.opCode).join(', ')}`,
            updatedBy: user?.nome || user?.email?.split('@')[0] || 'Sistema'
        };

        const updateData = {
            status: ORDER_STATUS.IN_PRODUCTION,
            trackingHistory: [...existingHistory, newHistoryEntry],
            updatedAt: new Date(),
            productionOrders: createdOrders.map(o => ({ id: o.id, opCode: o.opCode, productName: o.productName })),
            sentToProductionAt: new Date(),
            sentToProductionBy: user?.nome || user?.email?.split('@')[0] || 'Sistema'
        };

        await updateDocument(order.id, updateData);

        addToast(
            `${createdOrders.length} ordem(ns) de produção criada(s) com sucesso para o pedido ${order.code}. Status atualizado para "Em Produção".`,
            'Success'
        );

        await refreshData();
        return true;
    }

    if (errors.length > 0) {
        addToast(`${errors.length} erro(s) encontrado(s). Alguns produtos não puderam ser enviados para produção.`, 'Error');
    }

    return createdOrders.length > 0;
};

const updateOrderToPicking = async (orderId, user, addToast, updateDocument, refreshData, orders) => {
    try {
        const order = orders.find(o => o.id === orderId);
        if (!order) {
            addToast('Pedido não encontrado.', 'Error');
            return false;
        }

        addToast('Verificando disponibilidade em estoque...', 'Info');

        const productIds = order.items
            .filter(item => !item.isSample && !item.isBonus)
            .map(item => item.productId)
            .filter(id => id);

        if (productIds.length > 0) {
            const stockMap = new Map();

            for (let i = 0; i < productIds.length; i += 10) {
                const batch = productIds.slice(i, i + 10);
                const currentStockRef = collection(db, CURRENT_STOCK_COLLECTION);
                const q = query(currentStockRef, where('rawMaterialId', 'in', batch));
                const stockSnapshot = await getDocs(q);

                stockSnapshot.forEach(doc => {
                    const data = doc.data();
                    const existing = stockMap.get(data.rawMaterialId) || 0;
                    stockMap.set(data.rawMaterialId, existing + (data.quantity || 0));
                });
            }

            let hasShortage = false;
            const shortageItems = [];

            for (const item of order.items) {
                if (item.isSample || item.isBonus) {
                    continue;
                }

                const currentStock = stockMap.get(item.productId) || 0;
                const requestedQty = item.quantity || 0;

                if (currentStock < requestedQty) {
                    hasShortage = true;
                    shortageItems.push({
                        name: item.name,
                        code: item.code,
                        requested: requestedQty,
                        available: currentStock,
                        shortage: requestedQty - currentStock
                    });
                }
            }

            if (hasShortage) {
                const shortageMessage = shortageItems.map(i =>
                    `${i.code} - ${i.name}: solicitado ${i.requested}, disponível ${i.available.toFixed(3)} (falta ${i.shortage.toFixed(3)})`
                ).join('\n');

                addToast(`Não é possível enviar para separação. Itens com estoque insuficiente:\n${shortageMessage}`, 'Error');
                return false;
            }
        }

        const existingHistory = order.trackingHistory || [];
        const newHistoryEntry = {
            date: new Date().toLocaleDateString('pt-BR'),
            status: 'Pronto para Separação',
            description: 'Estoque verificado e disponível. Pedido encaminhado para separação.',
            updatedBy: user?.nome || user?.email?.split('@')[0] || 'Sistema'
        };

        const updateData = {
            status: ORDER_STATUS.READY_FOR_PICKING,
            trackingHistory: [...existingHistory, newHistoryEntry],
            updatedAt: new Date(),
            readyForPickingAt: new Date(),
            readyForPickingBy: user?.nome || user?.email?.split('@')[0] || 'Sistema',
            stockVerified: true,
            stockVerifiedAt: new Date(),
            stockVerifiedBy: user?.nome || user?.email?.split('@')[0] || 'Sistema'
        };

        const result = await updateDocument(orderId, updateData);

        if (result.success) {
            addToast(`Pedido ${order.code} encaminhado para separação! Status atualizado para "Pronto para Separação".`, 'Success');
            await refreshData();
            return true;
        } else {
            addToast(`Erro ao atualizar pedido: ${result.error}`, 'Error');
            return false;
        }
    } catch (error) {
        console.error('Error updating order to picking:', error);
        addToast(`Erro ao verificar estoque: ${error.message}`, 'Error');
        return false;
    }
};

// ============================================
// 7. NOTIFICATION FUNCTIONS
// ============================================

const notifyOrderCreator = async (orderId, status, orderCode, user, addNotification, actionType = 'approved') => {
    try {
        const orderDoc = await getDoc(doc(db, 'sales-orders', orderId));
        if (!orderDoc.exists()) return;

        const orderData = orderDoc.data();
        const creatorName = orderData.responsible;

        if (!creatorName) return;

        const usersSnapshot = await getDocs(collection(db, 'users'));
        let creatorUser = null;

        usersSnapshot.forEach(doc => {
            const data = doc.data();
            if (data.nome === creatorName || data.email === creatorName) {
                creatorUser = { uid: doc.id, ...data };
            }
        });

        if (!creatorUser || creatorUser.uid === user?.uid) return;

        const isApproved = status === ORDER_STATUS.APPROVED || status === ORDER_STATUS.IN_PRODUCTION;
        const config = getModuleConfig('aprovacao_de_pedido_de_venda');

        let title, message, type;
        if (actionType === 'returned') {
            title = '🔄 Pedido Retornado para Edição';
            message = `Seu pedido ${orderCode} foi retornado para edição por ${user?.nome || 'Aprovador'}`;
            type = 'sales_order_returned';
        } else if (isApproved) {
            title = '✅ Pedido Aprovado';
            message = `Seu pedido ${orderCode} foi aprovado por ${user?.nome || 'Aprovador'}`;
            type = 'sales_order_approved';
        } else {
            return;
        }

        const notification = {
            id: `so_${actionType}_${orderId}_${Date.now()}`,
            type: type,
            module: 'aprovacao_de_pedido_de_venda',
            moduleIcon: config?.icon || '📋',
            moduleColor: config?.color || '#28a745',
            moduleName: config?.name || 'Aprovação de Pedido de Venda',
            title: title,
            message: message,
            fullMessage: `Detalhes:\n• Pedido: ${orderCode}\n• Cliente: ${orderData.clientName || 'N/A'}\n• Valor Total: ${formatCurrencyUSD(orderData.calculatedValues?.total)}\n• Ação: ${actionType === 'returned' ? 'Retornado' : 'Aprovado'}\n• Por: ${user?.nome || 'Aprovador'}`,
            timestamp: new Date().toISOString(),
            read: false,
            priority: isApproved ? 'Alta' : 'Média',
            route: `/pedidos/pedido-de-venda`,
            data: {
                orderId: orderId,
                orderCode: orderCode,
                status: status,
                action: actionType,
                approvedBy: user?.nome || 'Aprovador'
            },
            userId: creatorUser.uid
        };

        addNotification(notification);

        try {
            await addDoc(collection(db, 'notifications'), {
                userId: creatorUser.uid,
                ...notification,
                createdAt: new Date()
            });
        } catch (err) {
            console.warn('Erro ao salvar notificação:', err);
        }

        console.log(`✅ Notificação enviada para ${creatorUser.nome} sobre ${orderCode} (${actionType})`);
    } catch (error) {
        console.error('❌ Erro ao notificar criador:', error);
    }
};

// ============================================
// 8. MAIN COMPONENT - DOLLAR NATIVE
// ============================================
const SaleOrderApprovalPage = () => {
    const can = usePagePermissions('aprovacao_de_pedido_de_venda');

    const [viewingOrder, setViewingOrder] = useState(null);
    const [stockCheckOrder, setStockCheckOrder] = useState(null);
    const [returnOrder, setReturnOrder] = useState(null);
    const [trackingOrder, setTrackingOrder] = useState(null);
    const [releaseOrder, setReleaseOrder] = useState(null);
    const [previewPackingListOrder, setPreviewPackingListOrder] = useState(null);
    const [invoicePreviewOrder, setInvoicePreviewOrder] = useState(null);
    const [currentPage, setCurrentPage] = useState(1);
    const [searchTerm, setSearchTerm] = useState('');
    const [statusFilter, setStatusFilter] = useState('pending');
    const [toasts, setToasts] = useState([]);
    const [actionLoading, setActionLoading] = useState(false);
    const [sendingToProduction, setSendingToProduction] = useState(false);
    const [confirmDialog, setConfirmDialog] = useState(null);
    const [packingOrder, setPackingOrder] = useState(null);

    const [allCategories, setAllCategories] = useState([]);
    const [allPalletTypes, setAllPalletTypes] = useState([]);
    const [loadingPalletTypes, setLoadingPalletTypes] = useState(false);

    // Invoice Info Modal states (replaces ExchangeRateModal)
    const [invoiceInfoModal, setInvoiceInfoModal] = useState(null);
    const [pendingInvoiceOrder, setPendingInvoiceOrder] = useState(null);
    const [invoiceInfoLoading, setInvoiceInfoLoading] = useState(false);

    const { currentUser } = useAuth();
    const { addNotification } = useNotifications();
    const { data: orders, loading: ordersLoading, updateDocument, refreshData } = useFirestore(
        SALES_ORDERS_COLLECTION,
        'createdAt',
        true
    );
    const { data: products } = useFirestore(PRODUCTS_COLLECTION);

    // Permissão para gerar documentos
    const canGenerateDocument = can.generate || false;

    // Fetch pallet types
    useEffect(() => {
        const fetchPalletTypes = async () => {
            setLoadingPalletTypes(true);
            try {
                const palletRef = collection(db, 'parameters', 'types', 'tipo-palete');
                const snapshot = await getDocs(palletRef);
                const pallets = [];
                snapshot.forEach(docSnap => {
                    pallets.push({
                        id: docSnap.id,
                        ...docSnap.data()
                    });
                });
                pallets.sort((a, b) => (a.name || '').localeCompare(b.name || ''));
                setAllPalletTypes(pallets);
            } catch (error) {
                console.error('Erro ao buscar tipos de palete:', error);
            } finally {
                setLoadingPalletTypes(false);
            }
        };
        fetchPalletTypes();
    }, []);

    useEffect(() => {
        const fetchCategories = async () => {
            try {
                const categoriesRef = collection(db, 'parameters/data/product-categories');
                const categoriesSnap = await getDocs(categoriesRef);
                const categoriesData = categoriesSnap.docs.map(doc => ({ id: doc.id, ...doc.data() }));
                setAllCategories(categoriesData);
            } catch (error) {
                console.error('Erro ao buscar categorias:', error);
            }
        };
        fetchCategories();
    }, []);

    const approvalOrders = useMemo(() => {
        if (!orders) return [];
        return orders.filter(order =>
            order.status === ORDER_STATUS.PENDING ||
            order.status === ORDER_STATUS.APPROVED ||
            order.status === ORDER_STATUS.IN_PRODUCTION ||
            order.status === ORDER_STATUS.PRODUCTION_COMPLETED ||
            order.status === ORDER_STATUS.READY_FOR_PICKING ||
            order.status === ORDER_STATUS.SHIPPED ||
            order.status === ORDER_STATUS.DELIVERED
        );
    }, [orders]);

    const addToast = (message, type) => {
        const id = Date.now();
        setToasts(prev => [...prev, { id, message, type }]);
        setTimeout(() => {
            setToasts(prev => prev.filter(toast => toast.id !== id));
        }, 5000);
    };

    // ============================================
    // HANDLE COMMERCIAL INVOICE - DOLLAR NATIVE (No Exchange Rate)
    // ============================================
    const handleCommercialInvoice = async (order) => {
        try {
            if (!order || !order.items || order.items.length === 0) {
                addToast('Pedido sem produtos para gerar a fatura.', 'warning');
                return;
            }

            // Get total in USD from the order
            const usdValues = order.usdValues || order.calculatedValues || {};
            const totalUSD = parseFloat(usdValues.total) || 0;

            if (totalUSD <= 0) {
                addToast('Valor total do pedido em USD não encontrado.', 'warning');
                return;
            }

            // Get client country
            let clientCountry = '';
            if (order.clientId) {
                try {
                    const clientRef = doc(db, 'clients', order.clientId);
                    const clientSnap = await getDoc(clientRef);
                    if (clientSnap.exists()) {
                        clientCountry = clientSnap.data().pais || '';
                    }
                } catch (err) {
                    console.error('Error fetching client country:', err);
                }
            }
            if (!clientCountry) {
                clientCountry = order.destination || '';
            }

            // Open Invoice Info Modal with USD values
            setInvoiceInfoModal({
                orderCode: order.code,
                totalValueUSD: totalUSD
            });
            setPendingInvoiceOrder({
                ...order,
                clientCountry: clientCountry,
                // Ensure USD values are preserved
                usdValues: usdValues
            });

        } catch (error) {
            console.error('Error preparing commercial invoice:', error);
            addToast(`Erro ao preparar fatura comercial: ${error.message}`, 'error');
        }
    };

    // ============================================
    // HANDLE CONFIRM INVOICE INFO (No Exchange Rate)
    // ============================================
    const handleConfirmInvoiceInfo = async (data) => {
        const { advancePaymentUSD, totalUSD, balanceUSD, incoterm } = data;
        const order = pendingInvoiceOrder;

        if (!order) {
            addToast('Pedido não encontrado.', 'error');
            setInvoiceInfoModal(null);
            setPendingInvoiceOrder(null);
            return;
        }

        setInvoiceInfoLoading(true);

        try {
            addToast(`Preparando fatura em USD...`, 'info');

            // Get product IDs from order items
            const productIds = order.items
                .map(item => item.productId)
                .filter(id => id);

            if (productIds.length === 0) {
                addToast('Nenhum ID de produto encontrado.', 'error');
                setInvoiceInfoLoading(false);
                setInvoiceInfoModal(null);
                setPendingInvoiceOrder(null);
                return;
            }

            const productsMap = new Map();

            for (const productId of productIds) {
                try {
                    const productRef = doc(db, 'products', productId);
                    const productSnap = await getDoc(productRef);

                    if (productSnap.exists()) {
                        const data = productSnap.data();
                        const technical = data.technical || {};

                        let unitsPerBox = 0;
                        if (technical.unitsPerBox !== undefined && technical.unitsPerBox !== null) {
                            unitsPerBox = parseInt(technical.unitsPerBox) || 0;
                        }
                        if (unitsPerBox === 0 && data.unitsPerBox !== undefined && data.unitsPerBox !== null) {
                            unitsPerBox = parseInt(data.unitsPerBox) || 0;
                        }

                        let netWeight = 1.0;
                        if (data.netWeight !== undefined && data.netWeight !== null) {
                            netWeight = parseFloat(data.netWeight) || 1.0;
                        } else if (technical.gramatura !== undefined && technical.gramatura !== null) {
                            netWeight = parseFloat(technical.gramatura) || 1.0;
                        }

                        productsMap.set(productId, {
                            id: productId,
                            unitsPerBox: unitsPerBox,
                            netWeight: netWeight,
                            code: data.code || '',
                            commercialName: data.commercialName || '',
                            technical: technical
                        });
                    }
                } catch (error) {
                    console.error(`Error fetching product ${productId}:`, error);
                }
            }

            const enrichedItems = order.items.map(item => {
                const product = productsMap.get(item.productId);
                const quantity = parseFloat(item.quantity) || 0;

                let unitsPerBox = 0;
                if (product && product.unitsPerBox > 0) {
                    unitsPerBox = product.unitsPerBox;
                }

                let boxesNeeded = 0;
                if (unitsPerBox > 0 && quantity > 0) {
                    boxesNeeded = Math.ceil(quantity / unitsPerBox);
                } else if (quantity > 0) {
                    boxesNeeded = 1;
                }

                return {
                    ...item,
                    unitsPerBox: unitsPerBox,
                    netWeight: product ? product.netWeight : (parseFloat(item.netWeight) || 1.0),
                    boxesNeeded: boxesNeeded
                };
            });

            const totalBoxes = enrichedItems.reduce((sum, item) => sum + (item.boxesNeeded || 0), 0);

            let shipperData = null;
            if (order.shipperName || order.shipperTaxId || order.shipperAddress) {
                shipperData = {
                    name: order.shipperName,
                    taxId: order.shipperTaxId,
                    address: order.shipperAddress
                };
            } else {
                const settingsRef = doc(db, 'parameters', 'global-settings');
                const settingsSnap = await getDoc(settingsRef);
                if (settingsSnap.exists()) {
                    const settings = settingsSnap.data();
                    shipperData = {
                        name: settings.shipperName || 'GRUPO B2E INDUSTRIA E COMERCIO DE COSMETICOS LTDA',
                        taxId: settings.shipperTaxId || '22.013.644/0001-70',
                        address: settings.shipperAddress || 'Av. Joao Paulo Ablas n 1400 - Galpao No. 4\nJardim da Gloria, Cotia/SP\nCEP 06711-250, SÃO PAULO, BRAZIL'
                    };
                }
            }

            let clientCountry = '';
            if (order.clientId) {
                try {
                    const clientRef = doc(db, 'clients', order.clientId);
                    const clientSnap = await getDoc(clientRef);
                    if (clientSnap.exists()) {
                        clientCountry = clientSnap.data().pais || '';
                    }
                } catch (err) {
                    console.error('Error fetching client country:', err);
                }
            }

            if (!clientCountry) {
                clientCountry = order.destination || '';
            }

            // Get USD values from the order
            const usdValues = order.usdValues || order.calculatedValues || {};

            // Build enriched order with USD values
            const enrichedOrder = {
                ...order,
                items: enrichedItems,
                totalBoxes: totalBoxes,
                advancePaymentUSD: advancePaymentUSD,
                totalUSD: totalUSD,
                balanceUSD: balanceUSD,
                clientCountry: clientCountry,
                incoterm: incoterm,
                // USD values - already in dollars
                usdValues: {
                    itemsTotal: parseFloat(usdValues.itemsTotal) || 0,
                    discountAmount: parseFloat(usdValues.discountAmount) || 0,
                    ticketDiscountAmount: parseFloat(usdValues.ticketDiscountAmount) || 0,
                    subtotalWithDiscounts: parseFloat(usdValues.subtotalWithDiscounts) || 0,
                    total: parseFloat(usdValues.total) || 0
                },
                clientDiscountPercent: order.clientDiscountPercent || 0,
                discountTicket: order.discountTicket || { type: 'amount', value: '' }
            };

            console.log('📊 Final order data (USD):', {
                totalUSD: enrichedOrder.totalUSD,
                advancePaymentUSD: enrichedOrder.advancePaymentUSD,
                balanceUSD: enrichedOrder.balanceUSD,
                incoterm: enrichedOrder.incoterm,
                usdValues: enrichedOrder.usdValues
            });

            setInvoiceInfoModal(null);
            setPendingInvoiceOrder(null);

            // Pass the enriched order to the invoice preview
            setInvoicePreviewOrder(enrichedOrder);

            addToast('Fatura comercial preparada com sucesso!', 'success');

        } catch (error) {
            console.error('Error generating commercial invoice:', error);
            addToast(`Erro ao gerar fatura comercial: ${error.message}`, 'error');
        } finally {
            setInvoiceInfoLoading(false);
        }
    };

    // ============================================
    // HANDLE PACKING LIST
    // ============================================
    const handlePackingList = async (order) => {
        try {
            console.log(`📦 Exporting Packing List for order ${order.code}`);
            console.log(`📦 Shipping Method: ${order.shippingMethod || 'not set'}`);
            console.log(`📦 Destination: ${order.destination || 'not set'}`);
            console.log(`📦 Pallet Type: ${order.palletTypeId || 'Auto'}`);
        } catch (error) {
            console.error('Error generating packing list:', error);
            addToast(`Erro ao gerar packing list: ${error.message}`, 'Error');
        }
    };

    // ============================================
    // FILTERED ORDERS
    // ============================================
    const filteredOrders = useMemo(() => {
        const lowerSearch = searchTerm.toLowerCase();
        return approvalOrders.filter(order => {
            const matchesSearch = (order.code && order.code.toLowerCase().includes(lowerSearch)) ||
                (order.clientName && order.clientName.toLowerCase().includes(lowerSearch));

            let matchesStatus = true;
            if (statusFilter === 'approved') {
                // Only show APPROVED status
                matchesStatus = order.status === ORDER_STATUS.APPROVED;
            } else if (statusFilter === 'pending') {
                // Show everything EXCEPT APPROVED
                matchesStatus = order.status !== ORDER_STATUS.APPROVED;
            }
            // 'all' shows everything

            return matchesSearch && matchesStatus;
        }).sort((a, b) => {
            const dateA = a.createdAt?.toDate ? a.createdAt.toDate() : new Date(a.createdAt);
            const dateB = b.createdAt?.toDate ? b.createdAt.toDate() : new Date(b.createdAt);
            return dateB - dateA;
        });
    }, [approvalOrders, searchTerm, statusFilter]);

    const totalPages = Math.ceil(filteredOrders.length / ITEMS_PER_PAGE);
    const currentOrders = useMemo(() => {
        const startIndex = (currentPage - 1) * ITEMS_PER_PAGE;
        return filteredOrders.slice(startIndex, startIndex + ITEMS_PER_PAGE);
    }, [filteredOrders, currentPage]);

    const handlePageChange = (newPage) => {
        if (newPage >= 1 && newPage <= totalPages) setCurrentPage(newPage);
    };

    // ============================================
    // HANDLERS
    // ============================================

    const handleReturnOrder = async (orderId, reason) => {
        setActionLoading(true);
        try {
            const order = orders.find(o => o.id === orderId);
            const existingHistory = order?.trackingHistory || [];

            const newHistoryEntry = {
                date: new Date().toLocaleDateString('pt-BR'),
                status: 'Retornado para Edição',
                description: `Pedido retornado para edição. Motivo: ${reason}`,
                updatedBy: currentUser?.nome || currentUser?.email?.split('@')[0] || 'Usuário'
            };

            const updateData = {
                status: ORDER_STATUS.DRAFT,
                returnedAt: new Date(),
                returnedBy: currentUser?.nome || currentUser?.email?.split('@')[0] || 'Usuário',
                returnReason: reason,
                trackingHistory: [...existingHistory, newHistoryEntry],
                updatedAt: new Date()
            };

            const result = await updateDocument(orderId, updateData);

            if (result.success) {
                addToast('Pedido retornado com sucesso! Agora ele pode ser editado e reenviado para aprovação.', 'Success');

                await notifyOrderCreator(orderId, ORDER_STATUS.DRAFT, order?.code, currentUser, addNotification, 'returned');

                await refreshData();
                setReturnOrder(null);
            } else {
                addToast(`Erro ao retornar pedido: ${result.error}`, 'Error');
            }
        } catch (error) {
            addToast('Ocorreu um erro inesperado.', 'Error');
        } finally {
            setActionLoading(false);
        }
    };

    const handleReleaseOrder = async (orderId, releaseNotes) => {
        setActionLoading(true);
        try {
            const order = orders.find(o => o.id === orderId);
            const existingHistory = order?.trackingHistory || [];

            let clientCountry = order.clientCountry || '';
            if (!clientCountry && order.clientId) {
                try {
                    const clientRef = doc(db, 'clients', order.clientId);
                    const clientSnap = await getDoc(clientRef);
                    if (clientSnap.exists()) {
                        clientCountry = clientSnap.data().pais || '';
                    }
                } catch (err) {
                    console.error('Error fetching client country:', err);
                }
            }

            const newHistoryEntry = {
                date: new Date().toLocaleDateString('pt-BR'),
                status: 'Aprovado',
                description: releaseNotes || 'Pedido aprovado na aprovação de vendas.',
                updatedBy: currentUser?.nome || currentUser?.email?.split('@')[0] || 'Usuário'
            };

            const updateData = {
                status: ORDER_STATUS.APPROVED,
                approvedAt: new Date(),
                approvedBy: currentUser?.nome || currentUser?.email?.split('@')[0] || 'Usuário',
                releaseNotes: releaseNotes,
                trackingHistory: [...existingHistory, newHistoryEntry],
                updatedAt: new Date(),
                clientCountry: clientCountry
            };

            const result = await updateDocument(orderId, updateData);

            if (result.success) {
                addToast(`Pedido ${order.code} aprovado com sucesso!`, 'Success');

                await notifyOrderCreator(orderId, ORDER_STATUS.APPROVED, order?.code, currentUser, addNotification, 'approved');

                await refreshData();
                setReleaseOrder(null);
            } else {
                addToast(`Erro ao aprovar pedido: ${result.error}`, 'Error');
            }
        } catch (error) {
            addToast('Ocorreu um erro inesperado.', 'Error');
        } finally {
            setActionLoading(false);
        }
    };

    const handlePackingConfirm = async (orderId, packingData) => {
        setActionLoading(true);
        try {
            const order = orders.find(o => o.id === orderId);
            const existingHistory = order?.trackingHistory || [];

            const newHistoryEntry = {
                date: new Date().toLocaleDateString('pt-BR'),
                status: 'Embalagem Concluída',
                description: `Embalagem finalizada. ${packingData.totalBoxes} caixas, ${packingData.unitOfMeasure} como unidade de medida. ${packingData.packingNotes ? `Obs: ${packingData.packingNotes}` : ''}`,
                updatedBy: currentUser?.nome || currentUser?.email?.split('@')[0] || 'Usuário'
            };

            // Update order status to "ready_for_billing" - this will make it appear in the billing module
            const updateData = {
                status: 'ready_for_billing',
                packingData: {
                    ...packingData,
                    packedBy: currentUser?.nome || currentUser?.email?.split('@')[0] || 'Usuário',
                    packedAt: new Date().toISOString()
                },
                unitOfMeasure: packingData.unitOfMeasure,
                grossWeight: packingData.grossWeight,
                netWeight: packingData.netWeight,
                totalBoxes: packingData.totalBoxes,
                packingNotes: packingData.packingNotes,
                trackingHistory: [...existingHistory, newHistoryEntry],
                updatedAt: new Date(),
                readyForBillingAt: new Date(),
                readyForBillingBy: currentUser?.nome || currentUser?.email?.split('@')[0] || 'Usuário'
            };

            const result = await updateDocument(orderId, updateData);

            if (result.success) {
                addToast(`Pedido ${order.code} enviado para Faturamento com sucesso!`, 'Success');

                // Notify the order creator
                await notifyOrderCreator(
                    orderId,
                    'ready_for_billing',
                    order?.code,
                    currentUser,
                    addNotification,
                    'packing_completed'
                );

                await refreshData();
                setPackingOrder(null);
            } else {
                addToast(`Erro ao enviar para faturamento: ${result.error}`, 'Error');
            }
        } catch (error) {
            console.error('Error confirming packing:', error);
            addToast(`Erro ao finalizar embalagem: ${error.message}`, 'Error');
        } finally {
            setActionLoading(false);
        }
    };

    const handleUpdateOrderStatus = async (orderId, newStatus, description) => {
        setActionLoading(true);
        try {
            const order = orders.find(o => o.id === orderId);
            const existingHistory = order?.trackingHistory || [];

            const newHistoryEntry = {
                date: new Date().toLocaleDateString('pt-BR'),
                status: getStatusLabel(newStatus),
                description: description,
                updatedBy: currentUser?.nome || currentUser?.email?.split('@')[0] || 'Usuário'
            };

            const updateData = {
                status: newStatus,
                trackingHistory: [...existingHistory, newHistoryEntry],
                updatedAt: new Date()
            };

            const result = await updateDocument(orderId, updateData);

            if (result.success) {
                addToast(`Status do pedido atualizado para ${getStatusLabel(newStatus)}`, 'Success');
                await refreshData();
            } else {
                addToast(`Erro ao atualizar status: ${result.error}`, 'Error');
            }
        } catch (error) {
            addToast('Ocorreu um erro inesperado.', 'Error');
        } finally {
            setActionLoading(false);
        }
    };

    const handleSendToProduction = async (order) => {
        if (sendingToProduction) return;

        setSendingToProduction(true);

        try {
            const success = await sendToProduction(
                order,
                currentUser,
                addToast,
                updateDocument,
                refreshData,
                allCategories
            );

            if (success) {
                await notifyOrderCreator(order.id, ORDER_STATUS.IN_PRODUCTION, order.code, currentUser, addNotification, 'approved');
            }
        } catch (error) {
            console.error('Error sending to production:', error);
            addToast(`Erro ao enviar pedido para produção: ${error.message}`, 'Error');
        } finally {
            setSendingToProduction(false);
        }
    };

    const handleUpdateToPicking = async (orderId) => {
        setActionLoading(true);
        try {
            await updateOrderToPicking(orderId, currentUser, addToast, updateDocument, refreshData, orders);
            setStockCheckOrder(null);
        } catch (error) {
            console.error('Error updating to picking:', error);
            addToast('Erro ao enviar para separação.', 'Error');
        } finally {
            setActionLoading(false);
        }
    };

    const showSkeleton = ordersLoading && approvalOrders?.length === 0;

    return (
        <div className={styles.pageContainer}>
            {/* Toast Container */}
            {toasts.length > 0 && (
                <div className={styles.toastContainer} aria-live="polite">
                    {toasts.map(toast => (
                        <Toast
                            key={toast.id}
                            message={toast.message}
                            type={toast.type}
                            onClose={() => setToasts(prev => prev.filter(t => t.id !== toast.id))}
                        />
                    ))}
                </div>
            )}

            {/* Invoice Info Modal - Replaces Exchange Rate Modal */}
            <InvoiceInfoModal
                isOpen={!!invoiceInfoModal}
                onClose={() => {
                    setInvoiceInfoModal(null);
                    setPendingInvoiceOrder(null);
                }}
                onConfirm={handleConfirmInvoiceInfo}
                orderCode={invoiceInfoModal?.orderCode}
                totalValueUSD={invoiceInfoModal?.totalValueUSD}
                isLoading={invoiceInfoLoading}
            />

            {/* Confirm Dialog */}
            <ConfirmDialog
                isOpen={!!confirmDialog}
                title={confirmDialog?.title || 'Confirmar'}
                message={confirmDialog?.message || ''}
                confirmText={confirmDialog?.confirmText || 'Confirmar'}
                cancelText="Cancelar"
                confirmVariant={confirmDialog?.confirmVariant || 'primary'}
                icon={confirmDialog?.icon}
                onConfirm={confirmDialog?.onConfirm || (() => { })}
                onCancel={() => setConfirmDialog(null)}
                isLoading={actionLoading}
            />

            {/* Modals */}
            {viewingOrder && (
                <ViewOrderModal
                    order={viewingOrder}
                    onClose={() => setViewingOrder(null)}
                />
            )}

            {stockCheckOrder && (
                <StockCheckModal
                    order={stockCheckOrder}
                    onClose={() => setStockCheckOrder(null)}
                    onUpdateToPicking={handleUpdateToPicking}
                    addToast={addToast}
                />
            )}

            {returnOrder && (
                <ReturnOrderModal
                    order={returnOrder}
                    onConfirm={handleReturnOrder}
                    onClose={() => setReturnOrder(null)}
                    addToast={addToast}
                />
            )}

            {packingOrder && (
                <PackingModal
                    order={packingOrder}
                    onClose={() => setPackingOrder(null)}
                    onConfirm={handlePackingConfirm}
                    addToast={addToast}
                    isLoading={actionLoading}
                />
            )}

            {trackingOrder && (
                <TrackingModal
                    order={trackingOrder}
                    onClose={() => setTrackingOrder(null)}
                    onUpdateStatus={handleUpdateOrderStatus}
                    addToast={addToast}
                />
            )}

            {releaseOrder && (
                <ReleaseOrderModal
                    order={releaseOrder}
                    onConfirm={handleReleaseOrder}
                    onClose={() => setReleaseOrder(null)}
                />
            )}

            {/* Packing List Preview Modal */}
            {previewPackingListOrder && (
                <PackingListPreview
                    order={previewPackingListOrder}
                    onClose={() => setPreviewPackingListOrder(null)}
                    canGenerate={canGenerateDocument}
                    onDownload={() => handlePackingList(previewPackingListOrder)}
                    addToast={addToast}
                    logoPath="/assets/grupo_logo.png"
                    itemsPerPage={7}
                />
            )}

            {/* Commercial Invoice Preview Modal - DOLLAR NATIVE */}
            {invoicePreviewOrder && (
                <CommercialInvoicePreview
                    order={invoicePreviewOrder}
                    onClose={() => setInvoicePreviewOrder(null)}
                    onDownload={() => {
                        addToast('Exportando fatura comercial...', 'Info');
                    }}
                    canGenerate={canGenerateDocument}
                    addToast={addToast}
                    logoPath="/assets/grupo_logo.png"
                    itemsPerPage={5}
                    clientCountry={invoicePreviewOrder.clientCountry || ''}
                />
            )}

            {/* Header */}
            <PageHeader
                title="Aprovação de Pedidos de Venda"
                actionText=""
                showAction={false}
            />

            {/* Search Bar with Status Filter */}
            <div className={styles.searchBar}>
                <div className={styles.searchBarWithFilter}>
                    <div className={styles.searchBarInput}>
                        <SearchBar
                            placeholder="Código do pedido, cliente..."
                            onSearch={(value) => {
                                setSearchTerm(value);
                                setCurrentPage(1);
                            }}
                            initialValue={searchTerm}
                            debounceDelay={300}
                            disabled={actionLoading}
                        />
                    </div>
                    <div className={styles.filterSelectWrapper}>
                        <select
                            className={styles.filterSelect}
                            value={statusFilter}
                            onChange={(e) => {
                                setStatusFilter(e.target.value);
                                setCurrentPage(1);
                            }}
                            disabled={actionLoading}
                        >
                            <option value="all">Todos os Status</option>
                            <option value="pending">Pendentes</option>
                            <option value="approved">Aprovados</option>
                        </select>
                    </div>
                </div>
            </div>

            {/* Table */}
            <div className={styles.tableContainer}>
                <table className={styles.table}>
                    <thead>
                        <tr>
                            <th>Código</th>
                            <th>Cliente</th>
                            <th>Data</th>
                            <th>Status</th>
                            <th style={{ width: '580px' }}>Ações</th>
                        </tr>
                    </thead>
                    <tbody>
                        {showSkeleton ? (
                            <tr>
                                <td colSpan="6">
                                    <TableSkeleton columns={6} rows={ITEMS_PER_PAGE} />
                                </td>
                            </tr>
                        ) : currentOrders && currentOrders.length > 0 ? (
                            currentOrders.map(order => (
                                <tr key={order.id} className={styles.tableRow}>
                                    <td><strong>{order.code}</strong></td>
                                    <td>{order.clientName}</td>
                                    <td>{formatDate(order.createdAt)}</td>
                                    <td>
                                        <StatusBadge status={order.status} />
                                    </td>
                                    <td>
                                        <div className={styles.actionsContainer}>
                                            {can.view && (
                                                <button
                                                    onClick={() => setViewingOrder(order)}
                                                    className={`${styles.actionButton} ${styles.viewButton}`}
                                                    disabled={actionLoading}
                                                    title="Visualizar Pedido"
                                                >
                                                    <FiEye size={14} />
                                                </button>
                                            )}
                                            {can.verify && (
                                                <button
                                                    onClick={() => setStockCheckOrder(order)}
                                                    className={`${styles.actionButton} ${styles.stockButton}`}
                                                    disabled={actionLoading}
                                                    title="Verificar Estoque"
                                                >
                                                    <FiPackage size={14} />
                                                </button>
                                            )}
                                            {can.return && (
                                                <button
                                                    onClick={() => setReturnOrder(order)}
                                                    className={`${styles.actionButton} ${styles.returnButton}`}
                                                    disabled={actionLoading}
                                                    title="Retornar para Edição"
                                                >
                                                    <FiArrowLeft size={14} />
                                                </button>
                                            )}
                                            {can.produce && (
                                                <button
                                                    onClick={() => handleSendToProduction(order)}
                                                    className={`${styles.actionButton} ${styles.productionButton}`}
                                                    disabled={actionLoading || sendingToProduction}
                                                    title="Enviar para Produção"
                                                >
                                                    {sendingToProduction ? <FiRefreshCw size={14} className={styles.spinner} /> : <FaIndustry size={14} />}
                                                </button>
                                            )}
                                            {can.track && (
                                                <button
                                                    onClick={() => setTrackingOrder(order)}
                                                    className={`${styles.actionButton} ${styles.trackButton}`}
                                                    disabled={actionLoading}
                                                    title="Rastrear Pedido"
                                                >
                                                    <FiMapPin size={14} />
                                                </button>
                                            )}
                                            {can.generate && (
                                                <button
                                                    onClick={() => handleCommercialInvoice(order)}
                                                    className={`${styles.actionButton} ${styles.invoiceButton}`}
                                                    disabled={actionLoading}
                                                    title="Commercial Invoice"
                                                >
                                                    <FiFileText size={14} />
                                                </button>
                                            )}
                                            {can.generate && (
                                                <button
                                                    onClick={() => setPreviewPackingListOrder(order)}
                                                    className={`${styles.actionButton} ${styles.packingButton}`}
                                                    disabled={actionLoading}
                                                    title="Packing List"
                                                >
                                                    <FiList size={14} />
                                                </button>
                                            )}
                                            {can.generate && order.status === ORDER_STATUS.APPROVED && (
                                                <button
                                                    onClick={() => setPackingOrder(order)}
                                                    className={`${styles.actionButton} ${styles.packingListButton}`}
                                                    disabled={actionLoading}
                                                    title="Iniciar Faturamento"
                                                >
                                                    <FaFileInvoiceDollar size={14} />
                                                </button>
                                            )}
                                            {can.approve && order.status === ORDER_STATUS.PENDING && (
                                                <button
                                                    onClick={() => setReleaseOrder(order)}
                                                    className={`${styles.actionButton} ${styles.confirmButton}`}
                                                    disabled={actionLoading}
                                                    title="Aprovar Pedido"
                                                >
                                                    <FiCheck size={14} />
                                                </button>
                                            )}
                                            {can.print && (
                                                <button
                                                    onClick={() => printFolhaPicking(order, addToast)}
                                                    className={`${styles.actionButton} ${styles.pickingButton}`}
                                                    disabled={actionLoading}
                                                    title="Folha de Picking"
                                                >
                                                    <FiClipboard size={14} />
                                                </button>
                                            )}
                                            {can.print && (
                                                <button
                                                    onClick={() => printRomaneio(order)}
                                                    className={`${styles.actionButton} ${styles.paymentButton}`}
                                                    disabled={actionLoading}
                                                    title="Imprimir Romaneio"
                                                >
                                                    <FaPrint size={14} />
                                                </button>
                                            )}
                                        </div>
                                    </td>
                                </tr>
                            ))
                        ) : (
                            <tr>
                                <td colSpan="6">
                                    <EmptyState
                                        icon="📋"
                                        title={searchTerm || statusFilter !== 'all' ? 'Nenhum pedido encontrado com os filtros aplicados.' : 'Nenhum pedido de venda para aprovação.'}
                                        message={searchTerm || statusFilter !== 'all' ? 'Tente ajustar os filtros de busca.' : 'Pedidos de venda pendentes aparecerão aqui.'}
                                    />
                                </td>
                            </tr>
                        )}
                    </tbody>
                </table>
            </div>

            {/* Pagination */}
            {!showSkeleton && totalPages > 1 && (
                <Pagination
                    currentPage={currentPage}
                    totalPages={totalPages}
                    totalItems={filteredOrders.length}
                    onPageChange={handlePageChange}
                    disabled={actionLoading}
                    itemsLabel="pedidos"
                />
            )}
        </div>
    );
};

export default SaleOrderApprovalPage;